import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { 
  ChevronLeft, ChevronRight, Save, Send, Eye, Upload, Image, Code, 
  FileText, Users, Trash2, Plus, AlertCircle, CheckCircle,
  X, Monitor, Smartphone, Palette, Layout, Type, Sparkles, Maximize2,
  Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight, Link,
  UserCheck, ChevronDown, Settings2, Table, MapPin, User, Building, Briefcase,
  Clock, Calendar, Zap, Pause, Play, XCircle, BarChart3, Timer,
  ImageIcon, Maximize, Minimize, Square, RefreshCw, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import LinkExtension from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import TiptapImage from '@tiptap/extension-image';
import TiptapUnderline from '@tiptap/extension-underline';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============== EMAIL LINK STYLING ==============
// Custom Link extension that forces inline styles for email compatibility
const LINK_STYLES = {
  color: '#1a73e8',
  textDecoration: 'underline'
};

const EmailLinkExtension = LinkExtension.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: `color: ${LINK_STYLES.color}; text-decoration: ${LINK_STYLES.textDecoration};`,
        parseHTML: element => element.getAttribute('style'),
        renderHTML: attributes => {
          // Always ensure link styling is applied
          return {
            style: `color: ${LINK_STYLES.color}; text-decoration: ${LINK_STYLES.textDecoration};`
          };
        }
      }
    };
  }
});

// ============== EMAIL IMAGE EXTENSION ==============
// Custom Image extension with email-safe responsive sizing and alignment
const EmailImageExtension = TiptapImage.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: '100%',
        parseHTML: element => element.getAttribute('width') || element.style.width || '100%',
        renderHTML: attributes => {
          return { width: attributes.width };
        }
      },
      align: {
        default: 'center',
        parseHTML: element => element.getAttribute('data-align') || 'center',
        renderHTML: attributes => {
          return { 'data-align': attributes.align };
        }
      },
      style: {
        default: null,
        parseHTML: element => element.getAttribute('style'),
        renderHTML: attributes => {
          const width = attributes.width || '100%';
          const align = attributes.align || 'center';
          
          // Email-safe inline styles
          let marginStyle = 'margin: 16px auto;'; // center by default
          if (align === 'left') marginStyle = 'margin: 16px 16px 16px 0;';
          if (align === 'right') marginStyle = 'margin: 16px 0 16px 16px;';
          
          const displayStyle = align === 'center' ? 'display: block;' : `display: block; float: ${align};`;
          
          return {
            style: `max-width: 100%; width: ${width}; height: auto; ${displayStyle} ${marginStyle} border-radius: 4px;`
          };
        }
      }
    };
  }
});

// ============== EMAIL STYLING PRESETS ==============
const defaultEmailStyles = {
  // Header styles
  useWncHeader: true,
  showLogo: true,
  headerBgColor: '#001428',
  headerAccentColor: '#d4a853',
  headerTextColor: '#ffffff',
  headerOverline: 'World Nuclear Congress',
  headerTitle: '',
  headerSubtitle: '',
  
  // Body styles
  bodyBgColor: '#ffffff',
  bodyTextColor: '#333333',
  bodyFontFamily: 'Arial, sans-serif',
  bodyFontSize: '16px',
  bodyLineHeight: '1.6',
  
  // Button styles
  buttonBgColor: '#d4a853',
  buttonTextColor: '#001428',
  buttonBorderRadius: '4px',
  
  // Footer styles — used by generateFooter(). All four are wired through to the HTML.
  footerBgColor: '#00457f',          // Main footer background
  footerTextColor: '#ffffff',        // Primary text (titles, "Event Details" value)
  footerMutedTextColor: '#cbd5e1',   // Secondary / muted text (links, tagline, address)
  footerBorderColor: '#1e3a5f',      // Divider line above the copyright row

  // Footer links — editable from Design → Footer Links. The CTA button and the quick-link
  // row both read from these. {{base_url}} is replaced at send time with the live site URL,
  // so internal paths work in both preview and production emails.
  footerRegisterUrl: 'https://utconferences.eventsair.com/cee/registration/Site/Register',
  footerRegisterLabel: 'Register for WNC',
  footerProgramUrl: '{{base_url}}/program',
  footerProgramLabel: 'Program',
  footerSponsorUrl: '{{base_url}}/sponsorship',
  footerSponsorLabel: 'Sponsor',
};

// ============== WNC PREMIUM HEADER TEMPLATE ==============
const generateWncHeader = (styles, logoUrl) => {
  return `
<!-- WNC Premium Email Header -->
<table width="100%" cellpadding="0" cellspacing="0" border="0" style="background: linear-gradient(135deg, ${styles.headerBgColor} 0%, #001f3f 50%, #001a2e 100%);">
  <tr>
    <td align="center" style="padding: 24px 16px;">
      <!-- Decorative top border -->
      <table width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width: 680px;">
        <tr>
          <td style="border-top: 2px solid ${styles.headerAccentColor}40; padding-top: 16px;">
            ${styles.showLogo ? `
            <!-- WNC Logo -->
            <table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-bottom: 12px;">
              <tr>
                <td align="center">
                  <img src="${logoUrl}" alt="World Nuclear Congress" width="138" height="138" style="display: block; max-width: 138px; height: auto;" />
                </td>
              </tr>
            </table>
            ` : ''}
            <!-- Overline -->
            <p style="margin: 0 0 12px 0; font-family: Arial, sans-serif; font-size: 11px; font-weight: 600; color: ${styles.headerAccentColor}; text-transform: uppercase; letter-spacing: 2px; text-align: center; word-wrap: break-word;">
              ${styles.headerOverline}
            </p>
            
            <!-- Title -->
            ${styles.headerTitle ? `
            <h1 style="margin: 0 0 8px 0; font-family: Georgia, 'Times New Roman', serif; font-size: 28px; font-weight: 600; color: ${styles.headerTextColor}; text-align: center; line-height: 1.3; word-wrap: break-word;">
              ${styles.headerTitle}
            </h1>
            ` : ''}
            
            <!-- Subtitle (right below title) -->
            ${styles.headerSubtitle ? `
            <p style="margin: 0 0 16px 0; font-family: Arial, sans-serif; font-size: 14px; color: #cbd5e1; text-align: center; font-weight: 300; line-height: 1.5; word-wrap: break-word;">
              ${styles.headerSubtitle}
            </p>
            ` : ''}
            
            <!-- Decorative line with diamond -->
            <table width="60%" cellpadding="0" cellspacing="0" border="0" align="center" style="margin: 0 auto 16px auto; max-width: 200px;">
              <tr>
                <td style="height: 1px; background: linear-gradient(to right, transparent, ${styles.headerAccentColor}80, transparent);"></td>
                <td width="10" style="text-align: center;">
                  <div style="width: 8px; height: 8px; background: ${styles.headerAccentColor}; transform: rotate(45deg); display: inline-block;"></div>
                </td>
                <td style="height: 1px; background: linear-gradient(to left, transparent, ${styles.headerAccentColor}80, transparent);"></td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>
<!-- End WNC Premium Email Header -->
`.trim();
};

// ============== EMAIL FOOTER TEMPLATE ==============
const generateFooter = (styles, logoUrl) => {
  const registrationUrl = styles.footerRegisterUrl || 'https://utconferences.eventsair.com/cee/registration/Site/Register';
  const registrationLabel = styles.footerRegisterLabel || 'Register for WNC';
  const programUrl = styles.footerProgramUrl || '{{base_url}}/program';
  const programLabel = styles.footerProgramLabel || 'Program';
  const sponsorshipUrl = styles.footerSponsorUrl || '{{base_url}}/sponsorship';
  const sponsorshipLabel = styles.footerSponsorLabel || 'Sponsor';

  // Pull colors from styles with safe fallbacks (older campaigns without these keys still render).
  const bg = styles.footerBgColor || '#00457f';
  const text = styles.footerTextColor || '#ffffff';
  const muted = styles.footerMutedTextColor || '#cbd5e1';
  const border = styles.footerBorderColor || '#1e3a5f';
  const accent = styles.headerAccentColor || '#d4a853';

  return `
<!-- Email Footer - WNC Inspired (Mobile Responsive) -->
<table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: ${bg};">
  <tr>
    <td align="center" style="padding: 32px 16px;">
      <table width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width: 680px;">
        
        <!-- Logo and Tagline - Centered -->
        <tr>
          <td align="center" style="padding-bottom: 24px;">
            <img src="${logoUrl}" alt="World Nuclear Congress" width="84" height="84" style="display: block; margin: 0 auto 12px auto; max-width: 84px; height: auto;" />
            <p style="margin: 0; font-family: Arial, sans-serif; font-size: 12px; color: ${muted}; line-height: 1.5; text-align: center;">
              The premier international forum for the nuclear industry.
            </p>
          </td>
        </tr>
        
        <!-- Event Details - Centered -->
        <tr>
          <td align="center" style="padding-bottom: 20px;">
            <p style="margin: 0 0 8px 0; font-family: Arial, sans-serif; font-size: 13px; font-weight: 700; color: ${accent};">
              Event Details
            </p>
            <p style="margin: 0 0 4px 0; font-family: Arial, sans-serif; font-size: 13px; font-weight: 600; color: ${text};">
              Coming February 2027
            </p>
            <p style="margin: 0; font-family: Arial, sans-serif; font-size: 12px; color: ${muted};">
              University of Tennessee, Knoxville, USA
            </p>
          </td>
        </tr>
        
        <!-- Quick Links - Centered Row -->
        <tr>
          <td align="center" style="padding-bottom: 24px;">
            <table cellpadding="0" cellspacing="0" border="0">
              <tr>
                <td style="padding: 0 12px;">
                  <a href="${registrationUrl}" style="font-family: Arial, sans-serif; font-size: 12px; color: ${text}; text-decoration: none; font-weight: 600;">
                    Register
                  </a>
                </td>
                <td style="color: ${muted};">|</td>
                <td style="padding: 0 12px;">
                  <a href="${programUrl}" style="font-family: Arial, sans-serif; font-size: 12px; color: ${muted}; text-decoration: none;">
                    ${programLabel}
                  </a>
                </td>
                <td style="color: ${muted};">|</td>
                <td style="padding: 0 12px;">
                  <a href="${sponsorshipUrl}" style="font-family: Arial, sans-serif; font-size: 12px; color: ${muted}; text-decoration: none;">
                    ${sponsorshipLabel}
                  </a>
                </td>
              </tr>
            </table>
          </td>
        </tr>
        
        <!-- CTA Button -->
        <tr>
          <td align="center" style="padding-bottom: 24px;">
            <table cellpadding="0" cellspacing="0" border="0">
              <tr>
                <td style="background: linear-gradient(135deg, ${accent} 0%, #b8943f 100%); border-radius: 6px;">
                  <a href="${registrationUrl}" style="display: inline-block; padding: 12px 24px; font-family: Arial, sans-serif; font-size: 13px; font-weight: 700; color: #001428; text-decoration: none; text-transform: uppercase; letter-spacing: 1px;">
                    ${registrationLabel}
                  </a>
                </td>
              </tr>
            </table>
          </td>
        </tr>
        
        <!-- Divider -->
        <tr>
          <td style="border-top: 1px solid ${border}; padding-top: 20px;">
            <!-- Contact Row -->
            <p style="margin: 0 0 12px 0; font-family: Arial, sans-serif; font-size: 12px; color: ${muted}; text-align: center;">
              <a href="mailto:contact@ceeleaders.com" style="color: ${muted}; text-decoration: none;">contact@ceeleaders.com</a>
              <span style="padding: 0 8px;">|</span>
              <a href="https://www.usceef.org" style="color: ${muted}; text-decoration: none;">usceef.org</a>
            </p>
            
            <!-- Tagline -->
            <p style="margin: 0 0 12px 0; font-family: Arial, sans-serif; font-size: 10px; color: ${muted}; opacity: 0.8; text-align: center; line-height: 1.5;">
              A U.S.-led, independent global forum dedicated to advancing nuclear leadership.
            </p>
            
            <!-- Copyright & Unsubscribe -->
            <p style="margin: 0; font-family: Arial, sans-serif; font-size: 10px; color: ${muted}; opacity: 0.8; text-align: center;">
              © 2027 World Nuclear Congress
              <span style="padding: 0 6px;">|</span>
              <a href="{{unsubscribe_url}}" style="color: ${accent}; text-decoration: underline;">Unsubscribe</a>
            </p>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>
<!-- End Email Footer -->
`.trim();
};

// ============== COLOR PICKER COMPONENT ==============
const ColorPicker = ({ label, value, onChange, presets = [] }) => {
  const defaultPresets = ['#001428', '#001f3f', '#d4a853', '#ffffff', '#333333', '#666666', '#f8f9fa', '#e5e7eb'];
  const colors = presets.length > 0 ? presets : defaultPresets;
  
  return (
    <div className="space-y-2">
      <Label className="text-sm">{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 rounded cursor-pointer border border-slate-200"
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-24 font-mono text-sm"
          placeholder="#000000"
        />
        <div className="flex gap-1 ml-2">
          {colors.slice(0, 4).map((color) => (
            <button
              key={color}
              onClick={() => onChange(color)}
              className={`w-6 h-6 rounded border-2 transition-all ${value === color ? 'border-blue-500 scale-110' : 'border-slate-200'}`}
              style={{ backgroundColor: color }}
              title={color}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// ============== FOOTER LINK EDITOR ==============
// Simple link editor with a label input, a URL input, and quick-pick chips for
// internal pages. Chips insert `{{base_url}}/<path>` which the backend replaces
// with the live site URL at send time. External URLs (e.g. Eventbrite) just go
// in the URL field as-is.
const INTERNAL_PAGE_CHIPS = [
  { label: 'Home', path: '/' },
  { label: 'Program', path: '/program' },
  { label: 'Sponsorship', path: '/sponsorship' },
  { label: 'Speakers', path: '/speakers' },
  { label: 'Golf Outing', path: '/golf-outing' },
];

const FooterLinkEditor = ({
  title,
  description,
  label,
  url,
  onLabelChange,
  onUrlChange,
  testIdPrefix,
}) => {
  const isExternal = !!url && !url.startsWith('{{base_url}}') && !url.startsWith('/');
  return (
    <div className="rounded-lg border border-gray-200 p-3 space-y-2 bg-white">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">{title}</p>
          {description && <p className="text-xs text-gray-500">{description}</p>}
        </div>
        <span
          className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
            isExternal
              ? 'text-indigo-700 bg-indigo-100'
              : 'text-emerald-700 bg-emerald-100'
          }`}
          title={isExternal ? 'Opens an external URL' : 'Links to a page on this site'}
          data-testid={`${testIdPrefix}-type-pill`}
        >
          {isExternal ? 'External URL' : 'Internal page'}
        </span>
      </div>
      <div>
        <Label className="text-xs">Button / Link Label</Label>
        <Input
          value={label || ''}
          onChange={(e) => onLabelChange(e.target.value)}
          placeholder="Register for WNC"
          data-testid={`${testIdPrefix}-label-input`}
        />
      </div>
      <div>
        <Label className="text-xs">Destination URL</Label>
        <Input
          value={url || ''}
          onChange={(e) => onUrlChange(e.target.value)}
          placeholder="https://... or /program"
          className="font-mono text-xs"
          data-testid={`${testIdPrefix}-url-input`}
        />
      </div>
      <div className="flex flex-wrap items-center gap-1 pt-1">
        <span className="text-[11px] text-gray-500 mr-1">Quick pick:</span>
        {INTERNAL_PAGE_CHIPS.map((p) => (
          <button
            key={p.path}
            type="button"
            onClick={() => onUrlChange(`{{base_url}}${p.path}`)}
            className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
              (url || '') === `{{base_url}}${p.path}` || url === p.path
                ? 'bg-[#00457f] text-white border-[#00457f]'
                : 'bg-white text-gray-700 border-gray-200 hover:border-[#00457f]'
            }`}
            data-testid={`${testIdPrefix}-chip-${p.path.replace(/\//g, '-') || 'home'}`}
          >
            {p.label}
          </button>
        ))}
      </div>
      {isExternal && (
        <p className="text-[11px] text-gray-500">
          External URL — opens exactly as pasted. Great for Eventbrite, Zoom, etc.
        </p>
      )}
      {!isExternal && url && (
        <p className="text-[11px] text-gray-500">
          Internal link — <code className="bg-gray-100 px-1 rounded">{'{{base_url}}'}</code> is replaced with the live site domain at send time.
        </p>
      )}
    </div>
  );
};

// ============== MAIN EMAIL EDITOR COMPONENT ==============
const EmailEditor = () => {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const fileInputRef = useRef(null);
  const csvInputRef = useRef(null);
  const token = localStorage.getItem('admin_token');

  const [campaign, setCampaign] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('content');
  const [editorMode, setEditorMode] = useState('wysiwyg');
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [pastedEmails, setPastedEmails] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [fullscreenPreview, setFullscreenPreview] = useState(false);

  // SendGrid verified-sender list (live from /api/email/senders).
  // Shape: { data: { verified_senders, domains, account_ok, error, cached_at }, loading, loadedAt }
  const [senderStatus, setSenderStatus] = useState({ data: null, loading: true, error: null });

  // CSV Column Mapping State
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [autoSplitName, setAutoSplitName] = useState(false);
  const [importingMapped, setImportingMapped] = useState(false);

  // Contact Lists state
  const [contactLists, setContactLists] = useState([]);
  const [selectedListIds, setSelectedListIds] = useState([]);
  const [wizardTags, setWizardTags] = useState([]);
  const [wizardSelectedTags, setWizardSelectedTags] = useState([]);
  const [wizardMatchAll, setWizardMatchAll] = useState(false);
  const [importingFromLists, setImportingFromLists] = useState(false);

  // Recipient Preview State
  const [previewRecipient, setPreviewRecipient] = useState(null);
  const [mergedPreview, setMergedPreview] = useState(null);
  const [showPersonalizationMenu, setShowPersonalizationMenu] = useState(false);
  const [showImageMenu, setShowImageMenu] = useState(false);
  const [testRecipientUsed, setTestRecipientUsed] = useState(null);

  // Fallback Rules State
  const [fallbackRules, setFallbackRules] = useState({
    first_name: { type: 'polite', value: 'there' },
    full_name: { type: 'fallback_field', value: 'first_name' },
    last_name: { type: 'blank', value: '' },
    title: { type: 'blank', value: '' },
    company: { type: 'blank', value: '' }
  });

  // Step Wizard State - Moved STEPS definition only, functions defined after formData
  const STEPS = [
    { id: 'content', label: 'Content', icon: FileText, description: 'Subject line & email body' },
    { id: 'design', label: 'Design', icon: Palette, description: 'Visual styling & layout' },
    { id: 'recipients', label: 'Recipients', icon: Users, description: 'Import & manage recipients' },
    { id: 'settings', label: 'Settings', icon: Settings2, description: 'Sender & delivery options' },
  ];
  const [completedSteps, setCompletedSteps] = useState({
    content: false,
    design: false,
    recipients: false,
    settings: false
  });
  const [highestVisitedStep, setHighestVisitedStep] = useState(0);

  // Delivery Settings State
  const [deliverySettings, setDeliverySettings] = useState({
    delivery_mode: 'immediate',
    batch_size: 50,
    interval_minutes: 30,
    start_at: null,
    sending_window_start: '',
    sending_window_end: '',
    max_per_day: null,
    timezone: 'America/New_York',
    warmup_preset: null,
    enable_open_tracking: true,
    enable_click_tracking: true
  });
  const [deliveryEstimate, setDeliveryEstimate] = useState(null);
  const [queuePreview, setQueuePreview] = useState(null);
  const [warmupPresets, setWarmupPresets] = useState([]);

  // Email styles state
  const [emailStyles, setEmailStyles] = useState(defaultEmailStyles);
  // Guards the auto-regenerate footer effect so it doesn't fire before campaign load
  const footerAutoRegenRef = useRef(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    preheader: '',
    html_body: '',
    header_image_url: '',
    from_name: '',
    from_email: '',
    reply_to: '',
    cc_emails: '',
    footer_html: ''
  });

  // Auto-regenerate footer_html whenever any footer style (colors + accent used by footer) changes.
  // Fires only after the initial campaign load has set the baseline footer_html, so we don't
  // overwrite the stored value before load is complete. We regenerate from the current styles
  // so what the user sees in the preview is exactly what gets sent.
  useEffect(() => {
    if (!footerAutoRegenRef.current) return;
    const logoUrl = `${API_URL}/assets/images/wnc-logo.png`;
    const nextFooter = generateFooter(emailStyles, logoUrl);
    setFormData(prev => (prev.footer_html === nextFooter ? prev : { ...prev, footer_html: nextFooter }));
  }, [
    emailStyles.footerBgColor,
    emailStyles.footerTextColor,
    emailStyles.footerMutedTextColor,
    emailStyles.footerBorderColor,
    emailStyles.headerAccentColor,
    emailStyles.footerRegisterUrl,
    emailStyles.footerRegisterLabel,
    emailStyles.footerProgramUrl,
    emailStyles.footerProgramLabel,
    emailStyles.footerSponsorUrl,
    emailStyles.footerSponsorLabel,
  ]);

  // TipTap Editor with email-compatible link styling
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Explicitly configure what to include/exclude from StarterKit
        // Link is disabled because we use our custom EmailLinkExtension
      }),
      TiptapUnderline,
      EmailLinkExtension.configure({
        openOnClick: false,
        HTMLAttributes: {
          style: `color: ${LINK_STYLES.color}; text-decoration: ${LINK_STYLES.textDecoration};`
        }
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      EmailImageExtension.configure({
        inline: false,
        allowBase64: false,
      }),
      TextStyle,
      Color,
    ],
    content: formData.html_body || '<p>Your email content here...</p>',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setFormData(prev => ({ ...prev, html_body: html }));
    },
  });

  // Update editor content when formData.html_body changes externally
  useEffect(() => {
    if (editor && formData.html_body && editor.getHTML() !== formData.html_body) {
      editor.commands.setContent(formData.html_body);
    }
  }, [editor, formData.html_body]);

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchCampaign();
    fetchRecipients();
    fetchContactLists();
  }, [campaignId, token, navigate]);

  // Fetch SendGrid verified senders once on mount. Cached server-side for 5 min.
  const fetchSenderStatus = useCallback(async (forceRefresh = false) => {
    setSenderStatus(prev => ({ ...prev, loading: true }));
    try {
      const url = `${API_URL}/api/email/senders${forceRefresh ? '?refresh=1' : ''}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`${r.status}`);
      const data = await r.json();
      setSenderStatus({ data, loading: false, error: data?.error || null });
    } catch (e) {
      setSenderStatus({ data: null, loading: false, error: 'Could not reach SendGrid.' });
    }
  }, [token]);
  useEffect(() => { if (token) fetchSenderStatus(false); }, [token, fetchSenderStatus]);

  // Helper: is a given address verified? Checks:
  //   1) It appears in verified_senders with verified=true
  //   2) OR its domain matches an authenticated domain with valid=true
  const verifiedSenders = senderStatus.data?.verified_senders || [];
  const validDomains = (senderStatus.data?.domains || []).filter(d => d.valid).map(d => (d.domain || '').toLowerCase());
  const senderStatusReady = !senderStatus.loading && senderStatus.data?.account_ok !== false;

  const checkEmailVerification = useCallback((email) => {
    const e = (email || '').trim().toLowerCase();
    if (!e || !senderStatusReady) return { known: false, verified: false, reason: null };
    const match = verifiedSenders.find(s => (s.from_email || '').toLowerCase() === e && s.verified);
    if (match) return { known: true, verified: true, via: 'single_sender', match };
    const domain = e.split('@')[1] || '';
    if (domain && validDomains.includes(domain)) {
      return { known: true, verified: true, via: 'domain_authentication', domain };
    }
    return { known: true, verified: false, reason: 'not_in_sendgrid' };
  }, [verifiedSenders, validDomains, senderStatusReady]);

  const fromVerification = checkEmailVerification(formData.from_email);


  // Step validation functions (defined after formData and recipients)
  const validateStep = useCallback((stepId) => {
    switch (stepId) {
      case 'content':
        return formData.subject?.trim().length > 0 && formData.html_body?.trim().length > 0;
      case 'design':
        return true; // Design is always valid (optional customization)
      case 'recipients':
        return recipients.length > 0;
      case 'settings':
        return formData.from_email?.trim().length > 0;
      default:
        return true;
    }
  }, [formData.subject, formData.html_body, formData.from_email, recipients.length]);

  const getStepValidationMessage = (stepId) => {
    switch (stepId) {
      case 'content':
        if (!formData.subject?.trim()) return 'Please enter a subject line';
        if (!formData.html_body?.trim()) return 'Please add email body content';
        return null;
      case 'recipients':
        if (recipients.length === 0) return 'Please add at least one recipient';
        return null;
      case 'settings':
        if (!formData.from_email?.trim()) return 'Please select or enter a sender email';
        return null;
      default:
        return null;
    }
  };

  const handleStepChange = async (stepId) => {
    const stepIndex = STEPS.findIndex(s => s.id === stepId);
    const currentIndex = STEPS.findIndex(s => s.id === activeTab);
    
    // Allow going back to any previous step, any completed step, or any step we've visited
    if (stepIndex <= currentIndex || stepIndex <= highestVisitedStep || completedSteps[stepId]) {
      // Auto-save before changing steps to prevent data loss
      try {
        await saveCampaign();
      } catch (error) {
        console.warn('Auto-save failed:', error);
      }
      
      setActiveTab(stepId);
      if (stepIndex > highestVisitedStep) {
        setHighestVisitedStep(stepIndex);
      }
    }
  };

  const handleContinue = async () => {
    const currentIndex = STEPS.findIndex(s => s.id === activeTab);
    const isValid = validateStep(activeTab);
    
    if (!isValid) {
      const message = getStepValidationMessage(activeTab);
      if (message) {
        toast.error(message);
      }
      return;
    }
    
    // Auto-save before moving to next step
    try {
      await saveCampaign();
    } catch (error) {
      // Continue even if save fails - user can save manually
      console.warn('Auto-save failed:', error);
    }
    
    // Mark current step as completed
    setCompletedSteps(prev => ({ ...prev, [activeTab]: true }));
    
    // Move to next step
    if (currentIndex < STEPS.length - 1) {
      const nextStep = STEPS[currentIndex + 1].id;
      setActiveTab(nextStep);
      setHighestVisitedStep(Math.max(highestVisitedStep, currentIndex + 1));
    }
  };

  const handleBack = () => {
    const currentIndex = STEPS.findIndex(s => s.id === activeTab);
    if (currentIndex > 0) {
      setActiveTab(STEPS[currentIndex - 1].id);
    }
  };

  // Update completed status when form data changes
  useEffect(() => {
    setCompletedSteps(prev => ({
      ...prev,
      content: validateStep('content'),
      recipients: validateStep('recipients'),
      settings: validateStep('settings')
    }));
  }, [formData.subject, formData.html_body, formData.from_email, recipients.length, validateStep]);

  const fetchCampaign = async () => {
    try {
      const response = await fetch(`${API_URL}/api/email/campaigns/${campaignId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        // If token expired or unauthorized, redirect to login
        if (response.status === 401) {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          toast.error('Session expired. Please log in again.');
          navigate('/admin/login');
          return;
        }
        throw new Error('Campaign not found');
      }
      
      const data = await response.json();
      setCampaign(data);
      
      // Parse stored email styles if available
      let storedStyles = defaultEmailStyles;
      if (data.email_styles) {
        try {
          storedStyles = { ...defaultEmailStyles, ...JSON.parse(data.email_styles) };
        } catch (e) {
          console.warn('Could not parse stored email styles');
        }
      }
      setEmailStyles(storedStyles);
      
      // Load delivery settings
      setDeliverySettings({
        delivery_mode: data.delivery_mode || 'immediate',
        batch_size: data.batch_size || 50,
        interval_minutes: data.interval_minutes || 30,
        start_at: data.start_at || null,
        sending_window_start: data.sending_window_start || '',
        sending_window_end: data.sending_window_end || '',
        max_per_day: data.max_per_day || null,
        timezone: data.timezone || 'America/New_York',
        warmup_preset: data.warmup_preset || null,
        enable_open_tracking: data.enable_open_tracking !== false,
        enable_click_tracking: data.enable_click_tracking !== false
      });
      
      // Auto-upgrade old footers to new WNC-branded footer
      const logoUrl = `${API_URL}/assets/images/wnc-logo.png`;
      const isOldFooter = data.footer_html && !data.footer_html.includes('background-color: #00457f');
      const footerHtml = isOldFooter ? generateFooter(storedStyles, logoUrl) : (data.footer_html || generateFooter(storedStyles, logoUrl));
      
      setFormData({
        name: data.name || '',
        subject: data.subject || '',
        preheader: data.preheader || '',
        html_body: data.html_body || '',
        header_image_url: data.header_image_url || '',
        from_name: data.from_name || 'World Nuclear Congress',
        from_email: data.from_email || 'WNC@ceeleaders.com',
        reply_to: data.reply_to || '',
        cc_emails: data.cc_emails || '',
        footer_html: footerHtml
      });
      // Enable auto-regeneration of footer_html on footer-color changes AFTER load,
      // so we don't overwrite anything with the default template before load completes.
      footerAutoRegenRef.current = true;
    } catch (error) {
      toast.error('Failed to load campaign');
      navigate('/admin/email');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecipients = async () => {
    try {
      const response = await fetch(`${API_URL}/api/email/campaigns/${campaignId}/recipients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecipients(data);
      }
    } catch (error) {
      console.error('Failed to fetch recipients:', error);
    }
  };

  const fetchContactLists = async () => {
    try {
      const response = await fetch(`${API_URL}/api/email/lists`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) setContactLists(await response.json());
    } catch (error) {
      console.error('Failed to fetch contact lists:', error);
    }
  };

  // Aggregate tags across selected lists (for the wizard tag filter)
  useEffect(() => {
    if (!selectedListIds.length) { setWizardTags([]); setWizardSelectedTags([]); return; }
    const load = async () => {
      try {
        const results = await Promise.all(selectedListIds.map(id =>
          fetch(`${API_URL}/api/email/lists/${id}/tags`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.ok ? r.json() : []).catch(() => [])
        ));
        const merged = new Map();
        for (const rows of results) {
          for (const r of (rows || [])) {
            const k = (r.tag || '').toLowerCase();
            if (!k) continue;
            if (merged.has(k)) merged.get(k).count += r.count;
            else merged.set(k, { tag: r.tag, count: r.count });
          }
        }
        setWizardTags(Array.from(merged.values()).sort((a, b) => b.count - a.count));
      } catch {
        setWizardTags([]);
      }
    };
    load();
  }, [selectedListIds, token]);

  const importFromLists = async () => {
    if (!selectedListIds.length) return;
    setImportingFromLists(true);
    try {
      const response = await fetch(`${API_URL}/api/email/lists/import-to-campaign`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaign_id: campaignId, list_ids: selectedListIds, tags: wizardSelectedTags, match_all_tags: wizardMatchAll }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(`Imported ${data.inserted} recipient(s) from ${selectedListIds.length} list(s)`);
        setImportResult({ ...data, valid: data.inserted, errors: [] });
        setSelectedListIds([]);
        setWizardSelectedTags([]);
        fetchRecipients();
      } else {
        toast.error(data.detail || 'List import failed');
      }
    } catch {
      toast.error('List import failed');
    }
    setImportingFromLists(false);
  };

  const saveCampaign = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/api/email/campaigns/${campaignId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          email_styles: JSON.stringify(emailStyles),
          // Delivery settings
          delivery_mode: deliverySettings.delivery_mode,
          batch_size: deliverySettings.batch_size,
          interval_minutes: deliverySettings.interval_minutes,
          start_at: deliverySettings.start_at,
          sending_window_start: deliverySettings.sending_window_start || null,
          sending_window_end: deliverySettings.sending_window_end || null,
          max_per_day: deliverySettings.max_per_day || null,
          timezone: deliverySettings.timezone,
          warmup_preset: deliverySettings.warmup_preset,
          // Tracking settings
          enable_open_tracking: deliverySettings.enable_open_tracking,
          enable_click_tracking: deliverySettings.enable_click_tracking
        })
      });
      
      if (!response.ok) throw new Error('Failed to save');
      
      const updated = await response.json();
      setCampaign(updated);
      toast.success('Campaign saved');
    } catch (error) {
      toast.error('Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  // Fetch delivery estimate
  const fetchDeliveryEstimate = async () => {
    if (recipients.length === 0) return;
    
    try {
      const response = await fetch(`${API_URL}/api/email/campaigns/${campaignId}/delivery-estimate`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDeliveryEstimate(data);
      }
    } catch (error) {
      console.error('Failed to fetch delivery estimate:', error);
    }
  };

  // Fetch queue preview
  const fetchQueuePreview = async () => {
    if (recipients.length === 0) return;
    
    try {
      const response = await fetch(`${API_URL}/api/email/campaigns/${campaignId}/queue-preview?num_batches=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setQueuePreview(data);
      }
    } catch (error) {
      console.error('Failed to fetch queue preview:', error);
    }
  };

  // Fetch warmup presets
  const fetchWarmupPresets = async () => {
    try {
      const response = await fetch(`${API_URL}/api/email/warmup-presets`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setWarmupPresets(data.presets || []);
      }
    } catch (error) {
      console.error('Failed to fetch warmup presets:', error);
    }
  };

  // Apply warmup preset
  const applyWarmupPreset = (presetId) => {
    const preset = warmupPresets.find(p => p.id === presetId);
    if (preset) {
      setDeliverySettings(prev => ({
        ...prev,
        batch_size: preset.batch_size,
        interval_minutes: preset.interval_minutes,
        warmup_preset: presetId
      }));
    }
  };

  // Update delivery estimate when settings change
  useEffect(() => {
    if (recipients.length > 0 && deliverySettings.delivery_mode !== 'immediate') {
      fetchDeliveryEstimate();
      fetchQueuePreview();
    }
  }, [recipients.length, deliverySettings.batch_size, deliverySettings.interval_minutes]);

  // Fetch warmup presets on mount
  useEffect(() => {
    fetchWarmupPresets();
  }, []);

  const uploadImage = async (file) => {
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await fetch(`${API_URL}/api/email/media/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });
      
      if (!response.ok) throw new Error('Upload failed');
      
      const data = await response.json();
      return `${API_URL}${data.url}`;
    } catch (error) {
      toast.error('Failed to upload image');
      return null;
    }
  };

  const handleHeaderImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const url = await uploadImage(file);
    if (url) {
      setFormData(prev => ({ ...prev, header_image_url: url }));
      toast.success('Header image uploaded');
    }
  };

  const insertImageIntoBody = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      
      const url = await uploadImage(file);
      if (url) {
        // Use Tiptap editor command to insert image with proper attributes
        if (editor) {
          editor.chain().focus().setImage({ 
            src: url, 
            alt: 'Image',
            width: '100%',
            align: 'center'
          }).run();
          toast.success('Image inserted - click to resize or align');
        } else {
          // Fallback if editor not ready
          const imgTag = `<img src="${url}" alt="Image" style="max-width: 100%; width: 100%; height: auto; display: block; margin: 16px auto; border-radius: 4px;" />`;
          setFormData(prev => ({
            ...prev,
            html_body: prev.html_body + imgTag
          }));
          toast.success('Image inserted');
        }
      }
    };
    input.click();
  };

  // State for image editing popover
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePopoverOpen, setImagePopoverOpen] = useState(false);

  // Update selected image attributes
  const updateImageAttributes = (attrs) => {
    if (editor && selectedImage) {
      editor.chain().focus().updateAttributes('image', attrs).run();
      setSelectedImage({ ...selectedImage, ...attrs });
    }
  };

  const insertButton = () => {
    const buttonHtml = `
<table cellpadding="0" cellspacing="0" border="0" style="margin: 20px 0;">
  <tr>
    <td style="background-color: ${emailStyles.buttonBgColor}; border-radius: ${emailStyles.buttonBorderRadius}; padding: 14px 28px;">
      <a href="#" style="color: ${emailStyles.buttonTextColor}; text-decoration: none; font-family: Arial, sans-serif; font-size: 16px; font-weight: 600; display: inline-block;">
        Button Text
      </a>
    </td>
  </tr>
</table>`;
    setFormData(prev => ({
      ...prev,
      html_body: prev.html_body + buttonHtml
    }));
    toast.success('Button inserted - update the link and text');
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setCsvFile(file);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      // Get CSV preview with headers and suggested mapping
      const response = await fetch(`${API_URL}/api/email/campaigns/${campaignId}/recipients/csv-preview`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });
      
      if (!response.ok) throw new Error('Preview failed');
      
      const preview = await response.json();
      setCsvPreview(preview);
      setColumnMapping(preview.suggested_mapping || {});
      setShowMappingModal(true);
    } catch (error) {
      toast.error('Failed to preview CSV');
    }
    
    // Reset the input so the same file can be selected again
    if (csvInputRef.current) csvInputRef.current.value = '';
  };

  // Import CSV with custom column mapping
  const importWithMapping = async () => {
    if (!csvFile || !columnMapping.email && columnMapping.email !== 0) {
      toast.error('Please map the email column');
      return;
    }
    
    setImportingMapped(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', csvFile);
    formDataUpload.append('column_mapping', JSON.stringify(columnMapping));
    formDataUpload.append('has_headers', csvPreview?.has_headers_detected ?? true);
    formDataUpload.append('auto_split_name', autoSplitName);
    formDataUpload.append('deduplicate', true);
    
    try {
      const response = await fetch(`${API_URL}/api/email/campaigns/${campaignId}/recipients/import-mapped`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });
      
      if (!response.ok) throw new Error('Import failed');
      
      const result = await response.json();
      setImportResult(result);
      fetchRecipients();
      setShowMappingModal(false);
      setCsvPreview(null);
      setCsvFile(null);
      toast.success(`Imported ${result.valid} recipients`);
    } catch (error) {
      toast.error('Failed to import CSV');
    } finally {
      setImportingMapped(false);
    }
  };

  // Insert personalization token into editor
  const insertPersonalizationToken = (token_name) => {
    if (editor) {
      editor.chain().focus().insertContent(`{{${token_name}}}`).run();
    }
    setShowPersonalizationMenu(false);
  };

  // Fetch mail merge preview for a specific recipient
  const fetchMergePreview = async (recipientId = null) => {
    try {
      const url = recipientId 
        ? `${API_URL}/api/email/campaigns/${campaignId}/preview-merge?recipient_id=${recipientId}`
        : `${API_URL}/api/email/campaigns/${campaignId}/preview-merge`;
        
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Preview failed');
      
      const data = await response.json();
      setMergedPreview(data);
      setPreviewRecipient(data.recipient);
    } catch (error) {
      console.error('Failed to fetch merge preview:', error);
    }
  };

  const importPastedEmails = async () => {
    if (!pastedEmails.trim()) {
      toast.error('Please enter some emails');
      return;
    }
    
    const formDataUpload = new FormData();
    formDataUpload.append('emails', pastedEmails);
    
    try {
      const response = await fetch(`${API_URL}/api/email/campaigns/${campaignId}/recipients/paste`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });
      
      if (!response.ok) throw new Error('Import failed');
      
      const result = await response.json();
      setImportResult(result);
      fetchRecipients();
      setPastedEmails('');
      toast.success(`Imported ${result.valid} recipients`);
    } catch (error) {
      toast.error('Failed to import emails');
    }
  };

  const clearRecipients = async () => {
    if (!window.confirm('Are you sure you want to clear all recipients?')) return;
    
    try {
      await fetch(`${API_URL}/api/email/campaigns/${campaignId}/recipients`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setRecipients([]);
      toast.success('Recipients cleared');
    } catch (error) {
      toast.error('Failed to clear recipients');
    }
  };

  const deleteRecipient = async (recipientId) => {
    try {
      await fetch(`${API_URL}/api/email/campaigns/${campaignId}/recipients/${recipientId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setRecipients(prev => prev.filter(r => r.id !== recipientId));
      toast.success('Recipient removed');
    } catch (error) {
      toast.error('Failed to remove recipient');
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter at least one test email address');
      return;
    }
    
    try {
      setSendingTest(true);
      await saveCampaign();
      
      const response = await fetch(`${API_URL}/api/email/campaigns/${campaignId}/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: testEmail })
      });
      
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        // detail can be a string or (for 422) a validation array
        let msg = data?.detail;
        if (Array.isArray(msg)) msg = msg.map(d => d.msg || JSON.stringify(d)).join('; ');
        throw new Error(msg || `Failed to send test email (${response.status})`);
      }
      
      // Prefer the backend's message so partial failures are surfaced
      toast.success(data?.message || `Test email sent to ${testEmail}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSendingTest(false);
    }
  };

  // Send test email with random recipient's data for mail merge preview
  const sendTestWithRandomRecipient = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }
    
    if (recipients.length === 0) {
      toast.error('No recipients available for testing');
      return;
    }
    
    try {
      setSendingTest(true);
      await saveCampaign();
      
      // Pick a random recipient
      const randomRecipient = recipients[Math.floor(Math.random() * recipients.length)];
      setTestRecipientUsed(randomRecipient);
      
      const response = await fetch(`${API_URL}/api/email/campaigns/${campaignId}/test-with-recipient`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          email: testEmail,
          recipient_id: randomRecipient.id
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send test email');
      }
      
      toast.success(`Test email sent to ${testEmail} using ${randomRecipient.first_name || randomRecipient.email}'s data`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSendingTest(false);
    }
  };

  const goToReview = async () => {
    // Pre-send guard: warn before leaving the editor if the From address can't be used.
    // We don't hard-block — the user may have just added it in SendGrid and refresh will
    // confirm — but we require an explicit confirmation so sends don't fail silently.
    const check = checkEmailVerification(formData.from_email);
    if (senderStatusReady && formData.from_email && check.known && !check.verified) {
      const proceed = window.confirm(
        `Heads up: "${formData.from_email}" is not a verified sender in SendGrid.\n\n` +
        `SendGrid will reject every send with "403 Forbidden" until you either:\n` +
        `  • verify this address under SendGrid → Sender Authentication, or\n` +
        `  • authenticate the sending domain, or\n` +
        `  • switch to a verified sender from the list above.\n\n` +
        `Continue to Review anyway?`
      );
      if (!proceed) return;
    }
    await saveCampaign();
    navigate(`/admin/email/review/${campaignId}`);
  };

  // Email clients (and Tailwind preflight in the preview) collapse <p> margins to zero.
  // Inject inline paragraph spacing so TipTap paragraphs render with visible breaks
  // both in-app and in Gmail/Outlook/Apple Mail. Empty paragraphs (TipTap uses them
  // for blank lines) get an explicit height so they act as spacers.
  const addParagraphSpacing = (html = '') => {
    if (!html) return html;
    const paragraphStyle =
      'margin: 0 0 16px 0; mso-margin-top-alt: 0; mso-margin-bottom-alt: 16px;';
    const emptyParagraphStyle =
      'margin: 0 0 16px 0; line-height: 16px; font-size: 16px;';
    return html
      // Empty paragraphs — TipTap produces <p></p> for a blank line
      .replace(/<p>(\s|<br\s*\/?>)*<\/p>/gi,
        `<p style="${emptyParagraphStyle}">&nbsp;</p>`)
      // Non-empty paragraphs without an inline style
      .replace(/<p(?![^>]*style=)([^>]*)>/gi,
        `<p$1 style="${paragraphStyle}">`)
      // Non-empty paragraphs that already have a style but no margin — append margin
      .replace(/<p([^>]*)style="([^"]*)"([^>]*)>/gi, (match, pre, style, post) => {
        if (/margin\s*:/i.test(style)) return match;
        return `<p${pre}style="${paragraphStyle} ${style}"${post}>`;
      });
  };

  const buildPreviewHtml = () => {
    let html = '';
    
    // Logo URL - use the production URL for emails
    const logoUrl = `${API_URL}/assets/images/wnc-logo.png`;
    
    // Add WNC header if enabled
    if (emailStyles.useWncHeader) {
      html += generateWncHeader(emailStyles, logoUrl);
    }
    
    // Add custom header image if set (when WNC header is disabled)
    if (!emailStyles.useWncHeader && formData.header_image_url) {
      html += `<img src="${formData.header_image_url}" alt="Header" style="width: 100%; max-width: 680px; height: auto; display: block; margin: 0 auto;" />`;
    }
    
    const spacedBody = addParagraphSpacing(formData.html_body) || '<p style="color: #94a3b8; font-style: italic;">Your email content here...</p>';

    // Add body wrapper with styles and proper margins (responsive)
    html += `
<table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: ${emailStyles.bodyBgColor};">
  <tr>
    <td align="center" style="padding: 24px 0;">
      <table width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width: 680px;">
        <tr>
          <td style="padding: 20px 28px; font-family: ${emailStyles.bodyFontFamily}; font-size: ${emailStyles.bodyFontSize}; line-height: ${emailStyles.bodyLineHeight}; color: ${emailStyles.bodyTextColor}; word-wrap: break-word;">
            ${spacedBody}
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`;
    
    // Add footer
    if (formData.footer_html) {
      html += formData.footer_html.replace('{{unsubscribe_url}}', '#unsubscribe');
    }
    
    return html;
  };

  // Build preview HTML with merged content for recipient preview
  const buildPreviewHtmlWithMerge = (mergedBody) => {
    let html = '';
    
    // Logo URL - use the production URL for emails
    const logoUrl = `${API_URL}/assets/images/wnc-logo.png`;
    
    // Add WNC header if enabled
    if (emailStyles.useWncHeader) {
      html += generateWncHeader(emailStyles, logoUrl);
    }
    
    // Add custom header image if set (when WNC header is disabled)
    if (!emailStyles.useWncHeader && formData.header_image_url) {
      html += `<img src="${formData.header_image_url}" alt="Header" style="width: 100%; max-width: 680px; height: auto; display: block; margin: 0 auto;" />`;
    }
    
    // Add body wrapper with styles and proper margins (responsive)
    html += `
<table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: ${emailStyles.bodyBgColor};">
  <tr>
    <td align="center" style="padding: 24px 0;">
      <table width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width: 680px;">
        <tr>
          <td style="padding: 20px 28px; font-family: ${emailStyles.bodyFontFamily}; font-size: ${emailStyles.bodyFontSize}; line-height: ${emailStyles.bodyLineHeight}; color: ${emailStyles.bodyTextColor}; word-wrap: break-word;">
            ${addParagraphSpacing(mergedBody) || '<p style="color: #94a3b8; font-style: italic;">Your email content here...</p>'}
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`;
    
    // Add footer
    if (formData.footer_html) {
      html += formData.footer_html.replace('{{unsubscribe_url}}', '#unsubscribe');
    }
    
    return html;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#00457f] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#00457f] text-white shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/email')}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              Back
            </button>
            <Input
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="bg-white/10 border-white/20 text-white placeholder:text-white/50 w-64"
              placeholder="Campaign Name"
            />
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={saveCampaign}
              disabled={saving}
              className="border-white/30 text-white hover:bg-white/10"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Draft'}
            </Button>
            <Button
              onClick={goToReview}
              disabled={recipients.length === 0}
              className="bg-yellow-500 hover:bg-yellow-600"
            >
              <Send className="w-4 h-4 mr-2" />
              Review & Send
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Panel - Editor */}
          <div className="space-y-6">
            {/* Step Indicator */}
            <div className="bg-white rounded-xl border shadow-sm p-4">
              <div className="flex items-center justify-between">
                {STEPS.map((step, index) => {
                  const StepIcon = step.icon;
                  const isActive = activeTab === step.id;
                  const isCompleted = completedSteps[step.id];
                  const isAccessible = index <= highestVisitedStep || isCompleted || index <= STEPS.findIndex(s => s.id === activeTab);
                  const currentIndex = STEPS.findIndex(s => s.id === activeTab);
                  
                  return (
                    <React.Fragment key={step.id}>
                      {/* Step Item */}
                      <button
                        onClick={() => isAccessible && handleStepChange(step.id)}
                        className={`flex flex-col items-center gap-1.5 transition-all ${
                          isAccessible ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                        }`}
                        disabled={!isAccessible}
                        data-testid={`step-${step.id}`}
                      >
                        <div className={`relative w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                          isActive
                            ? 'bg-[#00457f] text-white shadow-lg scale-110'
                            : isCompleted
                            ? 'bg-green-500 text-white'
                            : isAccessible
                            ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            : 'bg-gray-50 text-gray-400'
                        }`}>
                          {isCompleted && !isActive ? (
                            <CheckCircle className="w-5 h-5" />
                          ) : (
                            <span className="font-semibold text-sm">{index + 1}</span>
                          )}
                        </div>
                        <span className={`text-xs font-medium ${
                          isActive ? 'text-[#00457f]' : isCompleted ? 'text-green-600' : 'text-gray-500'
                        }`}>
                          {step.label}
                        </span>
                      </button>
                      
                      {/* Connector Line */}
                      {index < STEPS.length - 1 && (
                        <div className={`flex-1 h-0.5 mx-2 transition-colors ${
                          index < currentIndex || completedSteps[STEPS[index + 1].id]
                            ? 'bg-green-500'
                            : 'bg-gray-200'
                        }`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
              
              {/* Current Step Description */}
              <div className="mt-3 pt-3 border-t">
                <p className="text-sm text-gray-600 text-center">
                  <span className="font-medium text-gray-900">Step {STEPS.findIndex(s => s.id === activeTab) + 1}:</span>{' '}
                  {STEPS.find(s => s.id === activeTab)?.description}
                </p>
              </div>
            </div>

            <Tabs value={activeTab} onValueChange={handleStepChange}>
              <TabsList className="hidden">
                {STEPS.map(step => (
                  <TabsTrigger key={step.id} value={step.id}>{step.label}</TabsTrigger>
                ))}
              </TabsList>

              {/* ============== CONTENT TAB ============== */}
              <TabsContent value="content" className="space-y-4">
                {/* Subject & Preheader */}
                <Card>
                  <CardContent className="pt-6 space-y-4">
                    <div>
                      <Label htmlFor="subject">Subject Line *</Label>
                      <Input
                        id="subject"
                        value={formData.subject}
                        onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                        placeholder="Your email subject"
                      />
                    </div>
                    <div>
                      <Label htmlFor="preheader">Preheader Text</Label>
                      <Input
                        id="preheader"
                        value={formData.preheader}
                        onChange={(e) => setFormData(prev => ({ ...prev, preheader: e.target.value }))}
                        placeholder="Preview text shown in inbox"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* WNC Header Toggle */}
                <Card className="border-amber-200 bg-amber-50/50">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-amber-600" />
                        WNC Premium Header
                      </CardTitle>
                      <Switch
                        checked={emailStyles.useWncHeader}
                        onCheckedChange={(checked) => setEmailStyles(prev => ({ ...prev, useWncHeader: checked }))}
                      />
                    </div>
                  </CardHeader>
                  {emailStyles.useWncHeader && (
                    <CardContent className="space-y-4">
                      <p className="text-sm text-amber-700">
                        This adds a premium header matching the WNC website style.
                      </p>
                      
                      {/* Logo Toggle */}
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200">
                        <div className="flex items-center gap-3">
                          <img 
                            src="/assets/images/wnc-logo.png" 
                            alt="WNC Logo" 
                            className="w-10 h-10 rounded-full border border-amber-300"
                          />
                          <div>
                            <Label className="text-sm font-medium">Show WNC Logo</Label>
                            <p className="text-xs text-gray-500">Display logo at top center of header</p>
                          </div>
                        </div>
                        <Switch
                          checked={emailStyles.showLogo}
                          onCheckedChange={(checked) => setEmailStyles(prev => ({ ...prev, showLogo: checked }))}
                        />
                      </div>
                      
                      <div>
                        <Label>Header Title (optional)</Label>
                        <Input
                          value={emailStyles.headerTitle}
                          onChange={(e) => setEmailStyles(prev => ({ ...prev, headerTitle: e.target.value }))}
                          placeholder="e.g., Join Us at WNC 2026"
                        />
                      </div>
                      <div>
                        <Label>Header Subtitle (optional)</Label>
                        <Input
                          value={emailStyles.headerSubtitle}
                          onChange={(e) => setEmailStyles(prev => ({ ...prev, headerSubtitle: e.target.value }))}
                          placeholder="e.g., Coming February 2027 | Knoxville, TN"
                        />
                      </div>
                    </CardContent>
                  )}
                </Card>

                {/* Custom Header Image (when WNC header disabled) */}
                {!emailStyles.useWncHeader && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Custom Header Image</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {formData.header_image_url ? (
                        <div className="relative">
                          <img
                            src={formData.header_image_url}
                            alt="Header"
                            className="w-full h-32 object-cover rounded-lg"
                          />
                          <button
                            onClick={() => setFormData(prev => ({ ...prev, header_image_url: '' }))}
                            className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div
                          onClick={() => fileInputRef.current?.click()}
                          className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-[#00457f] transition-colors"
                        >
                          <Image className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-500">Click to upload header image</p>
                        </div>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleHeaderImageUpload}
                        className="hidden"
                      />
                    </CardContent>
                  </Card>
                )}

                {/* Email Body */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Email Body</CardTitle>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant={editorMode === 'wysiwyg' ? 'default' : 'outline'}
                          onClick={() => setEditorMode('wysiwyg')}
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          Visual
                        </Button>
                        <Button
                          size="sm"
                          variant={editorMode === 'html' ? 'default' : 'outline'}
                          onClick={() => setEditorMode('html')}
                        >
                          <Code className="w-4 h-4 mr-1" />
                          HTML
                        </Button>
                        <div className="border-l border-gray-200 pl-2 ml-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={insertImageIntoBody}
                            title="Insert Image"
                          >
                            <Image className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={insertButton}
                            title="Insert Button"
                            className="ml-1"
                          >
                            <Layout className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {editorMode === 'wysiwyg' ? (
                      <div className="border rounded-lg overflow-hidden">
                        {/* Rich Text Editor Toolbar */}
                        <div className="flex flex-wrap items-center gap-1 p-2 bg-gray-50 border-b">
                          <Button
                            size="sm"
                            variant={editor?.isActive('bold') ? 'default' : 'ghost'}
                            onClick={() => editor?.chain().focus().toggleBold().run()}
                            className="h-8 w-8 p-0"
                          >
                            <Bold className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant={editor?.isActive('italic') ? 'default' : 'ghost'}
                            onClick={() => editor?.chain().focus().toggleItalic().run()}
                            className="h-8 w-8 p-0"
                          >
                            <Italic className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant={editor?.isActive('underline') ? 'default' : 'ghost'}
                            onClick={() => editor?.chain().focus().toggleUnderline().run()}
                            className="h-8 w-8 p-0"
                          >
                            <Underline className="w-4 h-4" />
                          </Button>
                          <div className="w-px h-6 bg-gray-300 mx-1" />
                          <Button
                            size="sm"
                            variant={editor?.isActive('heading', { level: 1 }) ? 'default' : 'ghost'}
                            onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
                            className="h-8 px-2"
                          >
                            H1
                          </Button>
                          <Button
                            size="sm"
                            variant={editor?.isActive('heading', { level: 2 }) ? 'default' : 'ghost'}
                            onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                            className="h-8 px-2"
                          >
                            H2
                          </Button>
                          <div className="w-px h-6 bg-gray-300 mx-1" />
                          <Button
                            size="sm"
                            variant={editor?.isActive('bulletList') ? 'default' : 'ghost'}
                            onClick={() => editor?.chain().focus().toggleBulletList().run()}
                            className="h-8 w-8 p-0"
                          >
                            <List className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant={editor?.isActive('orderedList') ? 'default' : 'ghost'}
                            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                            className="h-8 w-8 p-0"
                          >
                            <ListOrdered className="w-4 h-4" />
                          </Button>
                          <div className="w-px h-6 bg-gray-300 mx-1" />
                          <Button
                            size="sm"
                            variant={editor?.isActive({ textAlign: 'left' }) ? 'default' : 'ghost'}
                            onClick={() => editor?.chain().focus().setTextAlign('left').run()}
                            className="h-8 w-8 p-0"
                          >
                            <AlignLeft className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant={editor?.isActive({ textAlign: 'center' }) ? 'default' : 'ghost'}
                            onClick={() => editor?.chain().focus().setTextAlign('center').run()}
                            className="h-8 w-8 p-0"
                          >
                            <AlignCenter className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant={editor?.isActive({ textAlign: 'right' }) ? 'default' : 'ghost'}
                            onClick={() => editor?.chain().focus().setTextAlign('right').run()}
                            className="h-8 w-8 p-0"
                          >
                            <AlignRight className="w-4 h-4" />
                          </Button>
                          <div className="w-px h-6 bg-gray-300 mx-1" />
                          <Button
                            size="sm"
                            variant={editor?.isActive('link') ? 'default' : 'ghost'}
                            onClick={() => {
                              const url = window.prompt('Enter URL:');
                              if (url) {
                                editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
                              }
                            }}
                            className="h-8 w-8 p-0"
                          >
                            <Link className="w-4 h-4" />
                          </Button>
                          <div className="w-px h-6 bg-gray-300 mx-1" />
                          {/* Image Controls Dropdown */}
                          <div className="relative">
                            <Button
                              size="sm"
                              variant={editor?.isActive('image') ? 'default' : 'outline'}
                              onClick={() => setShowImageMenu(!showImageMenu)}
                              className="h-8 px-2 gap-1 text-blue-700 border-blue-200 bg-blue-50 hover:bg-blue-100"
                              title="Image controls (select an image first)"
                            >
                              <Image className="w-4 h-4" />
                              <ChevronDown className="w-3 h-3" />
                            </Button>
                            {showImageMenu && (
                              <div className="absolute top-full right-0 mt-1 bg-white border rounded-lg shadow-lg z-50 w-56">
                                <div className="p-2 border-b bg-blue-50">
                                  <p className="text-xs font-semibold text-blue-700">Image Size & Alignment</p>
                                </div>
                                <div className="p-2 space-y-3">
                                  {/* Size Controls */}
                                  <div>
                                    <p className="text-xs text-gray-500 mb-1.5">Width</p>
                                    <div className="flex gap-1">
                                      {[
                                        { label: '100%', value: '100%' },
                                        { label: '75%', value: '75%' },
                                        { label: '50%', value: '50%' },
                                        { label: '25%', value: '25%' },
                                      ].map(({ label, value }) => (
                                        <button
                                          key={value}
                                          onClick={() => {
                                            editor?.chain().focus().updateAttributes('image', { width: value }).run();
                                            setShowImageMenu(false);
                                          }}
                                          className="flex-1 px-2 py-1.5 text-xs bg-gray-100 hover:bg-blue-100 rounded transition-colors"
                                        >
                                          {label}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                  {/* Alignment Controls */}
                                  <div>
                                    <p className="text-xs text-gray-500 mb-1.5">Alignment</p>
                                    <div className="flex gap-1">
                                      <button
                                        onClick={() => {
                                          editor?.chain().focus().updateAttributes('image', { align: 'left' }).run();
                                          setShowImageMenu(false);
                                        }}
                                        className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs bg-gray-100 hover:bg-blue-100 rounded"
                                      >
                                        <AlignLeft className="w-3 h-3" /> Left
                                      </button>
                                      <button
                                        onClick={() => {
                                          editor?.chain().focus().updateAttributes('image', { align: 'center' }).run();
                                          setShowImageMenu(false);
                                        }}
                                        className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs bg-gray-100 hover:bg-blue-100 rounded"
                                      >
                                        <AlignCenter className="w-3 h-3" /> Center
                                      </button>
                                      <button
                                        onClick={() => {
                                          editor?.chain().focus().updateAttributes('image', { align: 'right' }).run();
                                          setShowImageMenu(false);
                                        }}
                                        className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs bg-gray-100 hover:bg-blue-100 rounded"
                                      >
                                        <AlignRight className="w-3 h-3" /> Right
                                      </button>
                                    </div>
                                  </div>
                                  {/* Insert New Image */}
                                  <div className="pt-2 border-t">
                                    <button
                                      onClick={() => {
                                        insertImageIntoBody();
                                        setShowImageMenu(false);
                                      }}
                                      className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 rounded font-medium"
                                    >
                                      <Plus className="w-3 h-3" /> Insert New Image
                                    </button>
                                  </div>
                                </div>
                                <div className="p-2 border-t bg-gray-50 text-xs text-gray-500">
                                  Click on an image in the editor to select it, then choose size/alignment.
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="w-px h-6 bg-gray-300 mx-1" />
                          {/* Personalization Token Dropdown */}
                          <div className="relative">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setShowPersonalizationMenu(!showPersonalizationMenu)}
                              className="h-8 px-2 gap-1 text-amber-700 border-amber-200 bg-amber-50 hover:bg-amber-100"
                            >
                              <UserCheck className="w-4 h-4" />
                              <span className="text-xs hidden sm:inline">Personalize</span>
                              <ChevronDown className="w-3 h-3" />
                            </Button>
                            {showPersonalizationMenu && (
                              <div className="absolute top-full left-0 mt-1 bg-white border rounded-lg shadow-lg z-50 w-48">
                                <div className="p-2 border-b bg-amber-50">
                                  <p className="text-xs font-semibold text-amber-700">Insert Token</p>
                                </div>
                                <div className="p-1">
                                  {[
                                    { token: 'first_name', label: 'First Name', icon: User },
                                    { token: 'last_name', label: 'Last Name', icon: User },
                                    { token: 'full_name', label: 'Full Name', icon: User },
                                    { token: 'email', label: 'Email', icon: MapPin },
                                    { token: 'company', label: 'Company', icon: Building },
                                    { token: 'title', label: 'Title/Role', icon: Briefcase },
                                  ].map(({ token: t, label, icon: Icon }) => (
                                    <button
                                      key={t}
                                      onClick={() => insertPersonalizationToken(t)}
                                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-100 rounded"
                                    >
                                      <Icon className="w-4 h-4 text-gray-400" />
                                      <span>{label}</span>
                                      <code className="ml-auto text-xs text-gray-400">{`{{${t}}}`}</code>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Editor Content */}
                        <EditorContent 
                          editor={editor} 
                          className="prose max-w-none p-4 min-h-[300px] focus:outline-none [&_.ProseMirror]:min-h-[280px] [&_.ProseMirror]:outline-none [&_.ProseMirror_a]:text-[#1a73e8] [&_.ProseMirror_a]:underline [&_.ProseMirror_a]:cursor-pointer [&_.ProseMirror_img]:max-w-full [&_.ProseMirror_img]:h-auto [&_.ProseMirror_img]:rounded [&_.ProseMirror_img]:cursor-pointer [&_.ProseMirror_img]:transition-all [&_.ProseMirror_img.ProseMirror-selectednode]:ring-2 [&_.ProseMirror_img.ProseMirror-selectednode]:ring-blue-500 [&_.ProseMirror_img.ProseMirror-selectednode]:ring-offset-2"
                        />
                      </div>
                    ) : (
                      <Textarea
                        value={formData.html_body}
                        onChange={(e) => setFormData(prev => ({ ...prev, html_body: e.target.value }))}
                        className="min-h-[300px] font-mono text-sm"
                        placeholder="<p>Your HTML content here...</p>"
                      />
                    )}
                  </CardContent>
                </Card>

                {/* Step Navigation */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="text-sm text-gray-500">
                    {!completedSteps.content && (
                      <span className="text-amber-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {getStepValidationMessage('content') || 'Complete this step to continue'}
                      </span>
                    )}
                    {completedSteps.content && (
                      <span className="text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        Content ready
                      </span>
                    )}
                  </div>
                  <Button onClick={handleContinue} className="bg-[#00457f] hover:bg-[#003366]">
                    Continue to Design
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>

              </TabsContent>

              {/* ============== DESIGN TAB ============== */}
              <TabsContent value="design" className="space-y-4">
                {/* Header Styles */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Layout className="w-5 h-5" />
                      Header Styles
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ColorPicker
                      label="Background Color"
                      value={emailStyles.headerBgColor}
                      onChange={(color) => setEmailStyles(prev => ({ ...prev, headerBgColor: color }))}
                    />
                    <ColorPicker
                      label="Accent Color (Gold)"
                      value={emailStyles.headerAccentColor}
                      onChange={(color) => setEmailStyles(prev => ({ ...prev, headerAccentColor: color }))}
                      presets={['#d4a853', '#f59e0b', '#eab308', '#ca8a04', '#b45309', '#c9a227']}
                    />
                    <ColorPicker
                      label="Text Color"
                      value={emailStyles.headerTextColor}
                      onChange={(color) => setEmailStyles(prev => ({ ...prev, headerTextColor: color }))}
                    />
                    <div>
                      <Label>Overline Text</Label>
                      <Input
                        value={emailStyles.headerOverline}
                        onChange={(e) => setEmailStyles(prev => ({ ...prev, headerOverline: e.target.value }))}
                        placeholder="World Nuclear Congress 2026"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Body Styles */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Type className="w-5 h-5" />
                      Body Styles
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ColorPicker
                      label="Background Color"
                      value={emailStyles.bodyBgColor}
                      onChange={(color) => setEmailStyles(prev => ({ ...prev, bodyBgColor: color }))}
                    />
                    <ColorPicker
                      label="Text Color"
                      value={emailStyles.bodyTextColor}
                      onChange={(color) => setEmailStyles(prev => ({ ...prev, bodyTextColor: color }))}
                    />
                    <div>
                      <Label>Font Family</Label>
                      <select
                        value={emailStyles.bodyFontFamily}
                        onChange={(e) => setEmailStyles(prev => ({ ...prev, bodyFontFamily: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-md"
                      >
                        <option value="Arial, sans-serif">Arial</option>
                        <option value="Georgia, serif">Georgia (Serif)</option>
                        <option value="'Helvetica Neue', Helvetica, sans-serif">Helvetica</option>
                        <option value="Verdana, sans-serif">Verdana</option>
                        <option value="'Times New Roman', Times, serif">Times New Roman</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Font Size</Label>
                        <select
                          value={emailStyles.bodyFontSize}
                          onChange={(e) => setEmailStyles(prev => ({ ...prev, bodyFontSize: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-200 rounded-md"
                        >
                          <option value="14px">14px</option>
                          <option value="15px">15px</option>
                          <option value="16px">16px</option>
                          <option value="17px">17px</option>
                          <option value="18px">18px</option>
                        </select>
                      </div>
                      <div>
                        <Label>Line Height</Label>
                        <select
                          value={emailStyles.bodyLineHeight}
                          onChange={(e) => setEmailStyles(prev => ({ ...prev, bodyLineHeight: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-200 rounded-md"
                        >
                          <option value="1.4">Tight (1.4)</option>
                          <option value="1.5">Normal (1.5)</option>
                          <option value="1.6">Relaxed (1.6)</option>
                          <option value="1.8">Loose (1.8)</option>
                        </select>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Button Styles */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Button Styles</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ColorPicker
                      label="Button Background"
                      value={emailStyles.buttonBgColor}
                      onChange={(color) => setEmailStyles(prev => ({ ...prev, buttonBgColor: color }))}
                    />
                    <ColorPicker
                      label="Button Text"
                      value={emailStyles.buttonTextColor}
                      onChange={(color) => setEmailStyles(prev => ({ ...prev, buttonTextColor: color }))}
                    />
                    <div>
                      <Label>Border Radius</Label>
                      <select
                        value={emailStyles.buttonBorderRadius}
                        onChange={(e) => setEmailStyles(prev => ({ ...prev, buttonBorderRadius: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-md"
                      >
                        <option value="0px">Square</option>
                        <option value="4px">Slightly Rounded</option>
                        <option value="8px">Rounded</option>
                        <option value="20px">Pill</option>
                      </select>
                    </div>
                    {/* Button Preview */}
                    <div className="mt-4 p-4 bg-gray-100 rounded-lg text-center">
                      <span
                        style={{
                          backgroundColor: emailStyles.buttonBgColor,
                          color: emailStyles.buttonTextColor,
                          borderRadius: emailStyles.buttonBorderRadius,
                          padding: '12px 24px',
                          display: 'inline-block',
                          fontWeight: 600
                        }}
                      >
                        Button Preview
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* Footer Styles */}
                <Card data-testid="footer-styles-card">
                  <CardHeader>
                    <CardTitle className="text-base">Footer Styles</CardTitle>
                    <p className="text-xs text-gray-500 mt-1">
                      Controls the branded footer at the bottom of every email. Changes apply instantly in the preview on the right.
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ColorPicker
                      label="Footer Background"
                      value={emailStyles.footerBgColor}
                      onChange={(color) => setEmailStyles(prev => ({ ...prev, footerBgColor: color }))}
                      presets={['#00457f', '#001428', '#0f172a', '#1e293b', '#f8f9fa', '#ffffff', '#111827', '#d4a853']}
                    />
                    <ColorPicker
                      label="Footer Primary Text"
                      value={emailStyles.footerTextColor}
                      onChange={(color) => setEmailStyles(prev => ({ ...prev, footerTextColor: color }))}
                      presets={['#ffffff', '#f8fafc', '#e2e8f0', '#000000', '#111827', '#d4a853', '#334155', '#475569']}
                    />
                    <ColorPicker
                      label="Footer Muted / Secondary Text"
                      value={emailStyles.footerMutedTextColor}
                      onChange={(color) => setEmailStyles(prev => ({ ...prev, footerMutedTextColor: color }))}
                      presets={['#cbd5e1', '#94a3b8', '#64748b', '#9ca3af', '#6b7280', '#4b5563', '#e5e7eb', '#f3f4f6']}
                    />
                    <ColorPicker
                      label="Footer Divider / Border"
                      value={emailStyles.footerBorderColor}
                      onChange={(color) => setEmailStyles(prev => ({ ...prev, footerBorderColor: color }))}
                      presets={['#1e3a5f', '#334155', '#475569', '#e5e7eb', '#d1d5db', '#cbd5e1', '#374151', '#1f2937']}
                    />

                    {/* Live Footer Preview */}
                    <div
                      className="mt-4 rounded-lg overflow-hidden border"
                      style={{ backgroundColor: emailStyles.footerBgColor }}
                      data-testid="footer-preview"
                    >
                      <div className="p-5 text-center">
                        <p
                          className="text-xs font-bold uppercase tracking-wider mb-1"
                          style={{ color: emailStyles.headerAccentColor }}
                        >
                          Event Details
                        </p>
                        <p
                          className="text-sm font-semibold mb-1"
                          style={{ color: emailStyles.footerTextColor }}
                        >
                          Coming February 2027
                        </p>
                        <p
                          className="text-xs mb-4"
                          style={{ color: emailStyles.footerMutedTextColor }}
                        >
                          University of Tennessee, Knoxville, USA
                        </p>
                        <div
                          className="pt-4 mt-2"
                          style={{ borderTop: `1px solid ${emailStyles.footerBorderColor}` }}
                        >
                          <p
                            className="text-xs"
                            style={{ color: emailStyles.footerMutedTextColor }}
                          >
                            © 2027 World Nuclear Congress ·{' '}
                            <span style={{ color: emailStyles.headerAccentColor, textDecoration: 'underline' }}>
                              Unsubscribe
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        type="button"
                        onClick={() => setEmailStyles(prev => ({
                          ...prev,
                          footerBgColor: defaultEmailStyles.footerBgColor,
                          footerTextColor: defaultEmailStyles.footerTextColor,
                          footerMutedTextColor: defaultEmailStyles.footerMutedTextColor,
                          footerBorderColor: defaultEmailStyles.footerBorderColor,
                        }))}
                        data-testid="footer-reset-defaults"
                      >
                        Reset footer to defaults
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Footer Links */}
                <Card data-testid="footer-links-card">
                  <CardHeader>
                    <CardTitle className="text-base">Footer Links</CardTitle>
                    <p className="text-xs text-gray-500 mt-1">
                      The three links at the bottom of every email. Pick an internal page or paste any external URL (Eventbrite, etc.).
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <FooterLinkEditor
                      title="Register Now (CTA button + top link)"
                      description="The gold CTA button and the first quick link in the footer both point here."
                      label={emailStyles.footerRegisterLabel}
                      url={emailStyles.footerRegisterUrl}
                      onLabelChange={(v) => setEmailStyles(prev => ({ ...prev, footerRegisterLabel: v }))}
                      onUrlChange={(v) => setEmailStyles(prev => ({ ...prev, footerRegisterUrl: v }))}
                      testIdPrefix="footer-register"
                    />
                    <FooterLinkEditor
                      title="Program link"
                      label={emailStyles.footerProgramLabel}
                      url={emailStyles.footerProgramUrl}
                      onLabelChange={(v) => setEmailStyles(prev => ({ ...prev, footerProgramLabel: v }))}
                      onUrlChange={(v) => setEmailStyles(prev => ({ ...prev, footerProgramUrl: v }))}
                      testIdPrefix="footer-program"
                    />
                    <FooterLinkEditor
                      title="Sponsor link"
                      label={emailStyles.footerSponsorLabel}
                      url={emailStyles.footerSponsorUrl}
                      onLabelChange={(v) => setEmailStyles(prev => ({ ...prev, footerSponsorLabel: v }))}
                      onUrlChange={(v) => setEmailStyles(prev => ({ ...prev, footerSponsorUrl: v }))}
                      testIdPrefix="footer-sponsor"
                    />
                    <div className="flex justify-end pt-1">
                      <Button
                        variant="outline"
                        size="sm"
                        type="button"
                        onClick={() => setEmailStyles(prev => ({
                          ...prev,
                          footerRegisterUrl: defaultEmailStyles.footerRegisterUrl,
                          footerRegisterLabel: defaultEmailStyles.footerRegisterLabel,
                          footerProgramUrl: defaultEmailStyles.footerProgramUrl,
                          footerProgramLabel: defaultEmailStyles.footerProgramLabel,
                          footerSponsorUrl: defaultEmailStyles.footerSponsorUrl,
                          footerSponsorLabel: defaultEmailStyles.footerSponsorLabel,
                        }))}
                        data-testid="footer-links-reset"
                      >
                        Reset footer links to defaults
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Step Navigation */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <Button variant="outline" onClick={handleBack}>
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Back to Content
                  </Button>
                  <div className="text-sm text-green-600 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    Optional
                  </div>
                  <Button onClick={handleContinue} className="bg-[#00457f] hover:bg-[#003366]">
                    Continue to Recipients
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </TabsContent>

              {/* ============== RECIPIENTS TAB ============== */}
              <TabsContent value="recipients" className="space-y-4">
                {/* Import Options */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Import Recipients</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* CSV Upload */}
                    <div>
                      <Label>Upload CSV File</Label>
                      <p className="text-sm text-gray-500 mb-2">
                        Required column: email. Optional: first_name, last_name, company, role, type
                      </p>
                      <div
                        onClick={() => csvInputRef.current?.click()}
                        className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-[#00457f] transition-colors"
                      >
                        <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                        <p className="text-sm text-gray-500">Click to upload CSV</p>
                      </div>
                      <input
                        ref={csvInputRef}
                        type="file"
                        accept=".csv"
                        onChange={handleCSVUpload}
                        className="hidden"
                      />
                    </div>

                    {/* Paste Emails */}
                    <div>
                      <Label>Or Paste Emails</Label>
                      <p className="text-sm text-gray-500 mb-2">
                        Comma or newline separated
                      </p>
                      <Textarea
                        value={pastedEmails}
                        onChange={(e) => setPastedEmails(e.target.value)}
                        placeholder="email1@example.com, email2@example.com..."
                        className="min-h-[100px]"
                      />
                      <Button
                        onClick={importPastedEmails}
                        className="mt-2"
                        disabled={!pastedEmails.trim()}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Import Emails
                      </Button>
                    </div>

                    {/* Import from Contact List */}
                    <div className="border-t pt-4">
                      <div className="flex items-center justify-between mb-1">
                        <Label>Or Import from a Contact List</Label>
                        <a href="/admin/email/lists" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-700 hover:underline">Manage lists →</a>
                      </div>
                      <p className="text-sm text-gray-500 mb-2">
                        Reuse a saved list. Snapshot copy — future edits to the list won&apos;t affect this campaign.
                      </p>
                      {contactLists.length === 0 ? (
                        <div className="text-xs text-gray-500 bg-gray-50 border rounded p-3">
                          No saved lists yet. <a href="/admin/email/lists" target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">Create your first list</a> to import CSV once and reuse everywhere.
                        </div>
                      ) : (
                        <>
                          <div className="max-h-40 overflow-y-auto border rounded-lg divide-y" data-testid="contact-lists-picker">
                            {contactLists.map(l => (
                              <label key={l.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                                <input
                                  type="checkbox"
                                  checked={selectedListIds.includes(l.id)}
                                  onChange={(e) => setSelectedListIds(prev => e.target.checked ? [...prev, l.id] : prev.filter(x => x !== l.id))}
                                  data-testid={`list-checkbox-${l.id}`}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-gray-800 truncate">{l.name}</div>
                                  {l.description && <div className="text-xs text-gray-500 truncate">{l.description}</div>}
                                </div>
                                <div className="text-xs text-gray-500">{l.contact_count} contact{l.contact_count === 1 ? '' : 's'}</div>
                              </label>
                            ))}
                          </div>
                          {wizardTags.length > 0 && (
                            <div className="mt-2 p-3 border rounded-lg bg-white" data-testid="wizard-tag-filter">
                              <div className="flex items-center justify-between mb-1.5">
                                <div className="text-xs font-medium text-gray-700">Filter by tag <span className="text-gray-400 font-normal">(optional)</span></div>
                                <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
                                  <input type="checkbox" checked={wizardMatchAll} onChange={e => setWizardMatchAll(e.target.checked)} className="w-3 h-3" data-testid="wizard-match-all-toggle" />
                                  Match all (AND)
                                </label>
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {wizardTags.map(t => {
                                  const active = wizardSelectedTags.includes(t.tag);
                                  return (
                                    <button key={t.tag} onClick={() => setWizardSelectedTags(prev => active ? prev.filter(x => x !== t.tag) : [...prev, t.tag])} data-testid={`wizard-tag-${t.tag}`}
                                      className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${active ? 'bg-blue-900 border-blue-900 text-white' : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400'}`}>
                                      {t.tag} <span className="opacity-70">({t.count})</span>
                                    </button>
                                  );
                                })}
                              </div>
                              {wizardSelectedTags.length > 0 && (
                                <div className="text-xs text-gray-500 mt-1.5">
                                  Will import contacts with {wizardMatchAll ? 'ALL' : 'ANY'} of: <span className="font-medium text-gray-700">{wizardSelectedTags.join(', ')}</span>
                                </div>
                              )}
                            </div>
                          )}
                          <Button onClick={importFromLists} className="mt-2" disabled={!selectedListIds.length || importingFromLists} data-testid="import-from-lists-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            {importingFromLists ? 'Importing…' : `Import from ${selectedListIds.length || 0} list${selectedListIds.length === 1 ? '' : 's'}${wizardSelectedTags.length ? ` · ${wizardSelectedTags.length} tag${wizardSelectedTags.length === 1 ? '' : 's'}` : ''}`}
                          </Button>
                        </>
                      )}
                    </div>

                    {/* Import Result */}
                    {importResult && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold mb-2">Import Result</h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Valid: {importResult.valid}
                          </div>
                          <div className="flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-red-500" />
                            Invalid: {importResult.invalid}
                          </div>
                          <div className="text-gray-500">Duplicates: {importResult.duplicates}</div>
                          <div className="text-gray-500">Suppressed: {importResult.suppressed}</div>
                        </div>
                        {importResult.errors?.length > 0 && (
                          <div className="mt-2 text-sm text-red-600">
                            Errors: {importResult.errors.slice(0, 3).map(e => `Row ${e.row}: ${e.error}`).join(', ')}
                            {importResult.errors.length > 3 && ` (+${importResult.errors.length - 3} more)`}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Recipient List */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Recipients ({recipients.length})</CardTitle>
                      {recipients.length > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600"
                          onClick={clearRecipients}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Clear All
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {recipients.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">
                        No recipients added yet. Import via CSV or paste emails above.
                      </p>
                    ) : (
                      <div className="max-h-[400px] overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="text-left p-2">Email</th>
                              <th className="text-left p-2">Name</th>
                              <th className="w-10"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {recipients.map((recipient) => (
                              <tr key={recipient.id} className="border-b">
                                <td className="p-2">{recipient.email}</td>
                                <td className="p-2 text-gray-500">
                                  {recipient.first_name || recipient.last_name
                                    ? `${recipient.first_name || ''} ${recipient.last_name || ''}`.trim()
                                    : '-'}
                                </td>
                                <td className="p-2">
                                  <button
                                    onClick={() => deleteRecipient(recipient.id)}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Step Navigation */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <Button variant="outline" onClick={handleBack}>
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Back to Design
                  </Button>
                  <div className="text-sm">
                    {recipients.length === 0 ? (
                      <span className="text-amber-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        Add at least one recipient
                      </span>
                    ) : (
                      <span className="text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        {recipients.length} recipient{recipients.length !== 1 ? 's' : ''} ready
                      </span>
                    )}
                  </div>
                  <Button onClick={handleContinue} className="bg-[#00457f] hover:bg-[#003366]">
                    Continue to Settings
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </TabsContent>

              {/* ============== SETTINGS TAB ============== */}
              <TabsContent value="settings" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Sender Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Verified Senders (live from SendGrid) */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="block">Verified Senders in SendGrid</Label>
                        <button
                          type="button"
                          onClick={() => fetchSenderStatus(true)}
                          className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                          data-testid="refresh-senders-btn"
                        >
                          <RefreshCw className={`w-3 h-3 ${senderStatus.loading ? 'animate-spin' : ''}`} />
                          Refresh
                        </button>
                      </div>

                      {senderStatus.loading && !senderStatus.data && (
                        <p className="text-xs text-gray-500 italic">Loading SendGrid senders…</p>
                      )}

                      {senderStatus.data?.account_ok === false && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="sender-status-error">
                          <div className="font-semibold mb-1">Could not read SendGrid.</div>
                          <div>{senderStatus.data?.error || senderStatus.error || 'Check SENDGRID_API_KEY.'}</div>
                        </div>
                      )}

                      {senderStatusReady && verifiedSenders.length === 0 && validDomains.length === 0 && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                          No verified senders found in this SendGrid account. Add one in SendGrid → Settings → Sender Authentication, then click Refresh.
                        </div>
                      )}

                      {senderStatusReady && verifiedSenders.length > 0 && (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="verified-sender-grid">
                            {verifiedSenders.map((s) => {
                              const selected = formData.from_email?.toLowerCase() === (s.from_email || '').toLowerCase();
                              return (
                                <button
                                  key={s.from_email}
                                  type="button"
                                  onClick={() => setFormData(prev => ({
                                    ...prev,
                                    from_name: s.from_name || prev.from_name,
                                    from_email: s.from_email,
                                    reply_to: s.reply_to || prev.reply_to,
                                  }))}
                                  className={`text-left p-3 rounded-lg border transition-all ${
                                    selected
                                      ? 'border-[#00457f] bg-blue-50 ring-1 ring-[#00457f]'
                                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                  }`}
                                  data-testid={`verified-sender-${s.from_email}`}
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                      <p className="font-medium text-gray-900 text-sm truncate">
                                        {s.from_name || '(no name)'}
                                      </p>
                                      <p className="text-xs text-gray-500 truncate">{s.from_email}</p>
                                    </div>
                                    <span
                                      className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full"
                                      title="Verified in SendGrid"
                                    >
                                      <CheckCircle className="w-3 h-3" /> Verified
                                    </span>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                          {validDomains.length > 0 && (
                            <p className="text-xs text-gray-500 mt-2">
                              Also authenticated: any <code className="bg-gray-100 px-1 rounded">@{validDomains.join(', @')}</code> address.
                            </p>
                          )}
                        </>
                      )}
                    </div>

                    <div className="border-t pt-4">
                      <p className="text-sm font-medium text-gray-700 mb-3">Custom Sender Details</p>
                      <div className="space-y-3">
                        <div>
                          <Label htmlFor="from_name">From Name</Label>
                          <Input
                            id="from_name"
                            value={formData.from_name}
                            onChange={(e) => setFormData(prev => ({ ...prev, from_name: e.target.value }))}
                            placeholder="World Nuclear Congress"
                            data-testid="from-name-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="from_email">From Email</Label>
                          <div className="relative">
                            <Input
                              id="from_email"
                              type="email"
                              value={formData.from_email}
                              onChange={(e) => setFormData(prev => ({ ...prev, from_email: e.target.value }))}
                              placeholder="WNC@ceeleaders.com"
                              required
                              className={
                                formData.from_email && senderStatusReady && fromVerification.known && !fromVerification.verified
                                  ? 'pr-28 border-amber-400 focus-visible:ring-amber-300'
                                  : formData.from_email && fromVerification.verified
                                    ? 'pr-24'
                                    : ''
                              }
                              data-testid="from-email-input"
                            />
                            {formData.from_email && senderStatusReady && (
                              fromVerification.verified ? (
                                <span
                                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-[10px] font-semibold text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full"
                                  title={fromVerification.via === 'domain_authentication'
                                    ? `Any @${fromVerification.domain} address is authenticated in SendGrid`
                                    : 'This single sender is verified in SendGrid'}
                                  data-testid="from-email-verified-badge"
                                >
                                  <CheckCircle className="w-3 h-3" /> Verified
                                </span>
                              ) : (
                                <span
                                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded-full"
                                  title="Not a verified sender — SendGrid will reject this with 403"
                                  data-testid="from-email-unverified-badge"
                                >
                                  <AlertTriangle className="w-3 h-3" /> Unverified
                                </span>
                              )
                            )}
                          </div>
                          {formData.from_email && senderStatusReady && fromVerification.known && !fromVerification.verified && (
                            <div
                              className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
                              data-testid="from-email-unverified-warning"
                            >
                              <div className="font-semibold flex items-center gap-1 mb-1">
                                <AlertTriangle className="w-3.5 h-3.5" />
                                SendGrid will reject this address with 403 Forbidden.
                              </div>
                              <div className="space-y-1">
                                <div>
                                  <code className="bg-white/70 px-1 py-0.5 rounded">{formData.from_email}</code>
                                  {' '}is not a verified Single Sender, and its domain isn't authenticated in SendGrid.
                                </div>
                                <div className="text-amber-800">
                                  <span className="font-semibold">Fix:</span>{' '}
                                  pick a verified sender above,{' '}
                                  <a
                                    href="https://app.sendgrid.com/settings/sender_auth"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="underline font-semibold"
                                  >verify this address in SendGrid</a>, or authenticate the domain.
                                </div>
                              </div>
                            </div>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            Must be a verified sender in SendGrid (or on an authenticated domain).
                          </p>
                        </div>
                        <div>
                          <Label htmlFor="reply_to">Reply-To Email (optional)</Label>
                          <Input
                            id="reply_to"
                            type="email"
                            value={formData.reply_to}
                            onChange={(e) => setFormData(prev => ({ ...prev, reply_to: e.target.value }))}
                            placeholder="contact@ceeleaders.com"
                            data-testid="reply-to-input"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Replies will be sent to this address instead of From Email
                          </p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="cc_emails">CC Recipients (optional)</Label>
                      <Input
                        id="cc_emails"
                        value={formData.cc_emails}
                        onChange={(e) => setFormData(prev => ({ ...prev, cc_emails: e.target.value }))}
                        placeholder="email1@example.com, email2@example.com"
                        data-testid="cc-emails-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Comma-separated list of email addresses to CC on all emails
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Delivery Settings */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Delivery Settings
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Delivery Mode Selector */}
                    <div>
                      <Label className="text-sm font-medium">Delivery Mode</Label>
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        <button
                          onClick={() => setDeliverySettings(prev => ({ ...prev, delivery_mode: 'immediate' }))}
                          className={`p-3 rounded-lg border text-left transition-all ${
                            deliverySettings.delivery_mode === 'immediate'
                              ? 'border-[#00457f] bg-[#00457f]/5 ring-1 ring-[#00457f]'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <Zap className={`w-4 h-4 mb-1 ${deliverySettings.delivery_mode === 'immediate' ? 'text-[#00457f]' : 'text-gray-400'}`} />
                          <p className="font-medium text-sm">Immediate</p>
                          <p className="text-xs text-gray-500">Send all at once</p>
                        </button>
                        <button
                          onClick={() => setDeliverySettings(prev => ({ ...prev, delivery_mode: 'throttled' }))}
                          className={`p-3 rounded-lg border text-left transition-all ${
                            deliverySettings.delivery_mode === 'throttled'
                              ? 'border-[#00457f] bg-[#00457f]/5 ring-1 ring-[#00457f]'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <Timer className={`w-4 h-4 mb-1 ${deliverySettings.delivery_mode === 'throttled' ? 'text-[#00457f]' : 'text-gray-400'}`} />
                          <p className="font-medium text-sm">Throttled</p>
                          <p className="text-xs text-gray-500">Batches over time</p>
                        </button>
                        <button
                          onClick={() => setDeliverySettings(prev => ({ ...prev, delivery_mode: 'scheduled_throttled' }))}
                          className={`p-3 rounded-lg border text-left transition-all ${
                            deliverySettings.delivery_mode === 'scheduled_throttled'
                              ? 'border-[#00457f] bg-[#00457f]/5 ring-1 ring-[#00457f]'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <Calendar className={`w-4 h-4 mb-1 ${deliverySettings.delivery_mode === 'scheduled_throttled' ? 'text-[#00457f]' : 'text-gray-400'}`} />
                          <p className="font-medium text-sm">Scheduled</p>
                          <p className="text-xs text-gray-500">Start later + batches</p>
                        </button>
                      </div>
                    </div>

                    {/* Throttled/Scheduled Settings */}
                    {deliverySettings.delivery_mode !== 'immediate' && (
                      <div className="space-y-4 pt-4 border-t">
                        {/* Warmup Presets */}
                        <div>
                          <Label className="text-sm">Warmup Preset</Label>
                          <div className="flex gap-2 mt-2">
                            {warmupPresets.map(preset => (
                              <button
                                key={preset.id}
                                onClick={() => applyWarmupPreset(preset.id)}
                                className={`px-3 py-2 rounded-md text-sm border transition-all ${
                                  deliverySettings.warmup_preset === preset.id
                                    ? 'border-[#00457f] bg-[#00457f]/5 text-[#00457f]'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                {preset.name}
                              </button>
                            ))}
                            <button
                              onClick={() => setDeliverySettings(prev => ({ ...prev, warmup_preset: 'custom' }))}
                              className={`px-3 py-2 rounded-md text-sm border transition-all ${
                                deliverySettings.warmup_preset === 'custom'
                                  ? 'border-[#00457f] bg-[#00457f]/5 text-[#00457f]'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              Custom
                            </button>
                          </div>
                        </div>

                        {/* Batch Size & Interval */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="batch_size" className="text-sm">Batch Size</Label>
                            <Input
                              id="batch_size"
                              type="number"
                              min={1}
                              max={500}
                              value={deliverySettings.batch_size}
                              onChange={(e) => setDeliverySettings(prev => ({
                                ...prev,
                                batch_size: parseInt(e.target.value) || 50,
                                warmup_preset: 'custom'
                              }))}
                              className="mt-1"
                            />
                            <p className="text-xs text-gray-500 mt-1">Max 500 per batch</p>
                          </div>
                          <div>
                            <Label htmlFor="interval" className="text-sm">Interval (minutes)</Label>
                            <Input
                              id="interval"
                              type="number"
                              min={1}
                              max={1440}
                              value={deliverySettings.interval_minutes}
                              onChange={(e) => setDeliverySettings(prev => ({
                                ...prev,
                                interval_minutes: parseInt(e.target.value) || 30,
                                warmup_preset: 'custom'
                              }))}
                              className="mt-1"
                            />
                            <p className="text-xs text-gray-500 mt-1">Min 1 minute</p>
                          </div>
                        </div>

                        {/* Scheduled Start Time */}
                        {deliverySettings.delivery_mode === 'scheduled_throttled' && (
                          <div>
                            <Label htmlFor="start_at" className="text-sm">Start Time</Label>
                            <Input
                              id="start_at"
                              type="datetime-local"
                              value={deliverySettings.start_at ? new Date(deliverySettings.start_at).toISOString().slice(0, 16) : ''}
                              onChange={(e) => setDeliverySettings(prev => ({
                                ...prev,
                                start_at: e.target.value ? new Date(e.target.value).toISOString() : null
                              }))}
                              className="mt-1"
                            />
                          </div>
                        )}

                        {/* Daily Sending Window */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="window_start" className="text-sm">Window Start (optional)</Label>
                            <Input
                              id="window_start"
                              type="time"
                              value={deliverySettings.sending_window_start}
                              onChange={(e) => setDeliverySettings(prev => ({
                                ...prev,
                                sending_window_start: e.target.value
                              }))}
                              className="mt-1"
                              placeholder="09:00"
                            />
                          </div>
                          <div>
                            <Label htmlFor="window_end" className="text-sm">Window End (optional)</Label>
                            <Input
                              id="window_end"
                              type="time"
                              value={deliverySettings.sending_window_end}
                              onChange={(e) => setDeliverySettings(prev => ({
                                ...prev,
                                sending_window_end: e.target.value
                              }))}
                              className="mt-1"
                              placeholder="18:00"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-gray-500">
                          Daily window restricts sending to specific hours (e.g., business hours)
                        </p>

                        {/* Max Per Day */}
                        <div>
                          <Label htmlFor="max_per_day" className="text-sm">Max Sends Per Day (optional)</Label>
                          <Input
                            id="max_per_day"
                            type="number"
                            min={0}
                            value={deliverySettings.max_per_day || ''}
                            onChange={(e) => setDeliverySettings(prev => ({
                              ...prev,
                              max_per_day: e.target.value ? parseInt(e.target.value) : null
                            }))}
                            className="mt-1"
                            placeholder="No limit"
                          />
                        </div>

                        {/* Delivery Estimate */}
                        {recipients.length > 0 && deliveryEstimate && (
                          <div className="bg-[#00457f]/5 rounded-lg p-4 mt-4">
                            <h4 className="font-medium text-sm flex items-center gap-2 mb-3">
                              <BarChart3 className="w-4 h-4 text-[#00457f]" />
                              Delivery Estimate
                            </h4>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-gray-500">Total Recipients</p>
                                <p className="font-semibold">{deliveryEstimate.total_recipients}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Total Batches</p>
                                <p className="font-semibold">{deliveryEstimate.total_batches}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Batch Size</p>
                                <p className="font-semibold">{deliveryEstimate.batch_size} emails</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Estimated Completion</p>
                                <p className="font-semibold">
                                  {new Date(deliveryEstimate.estimated_completion_time).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Queue Preview */}
                        {queuePreview && queuePreview.next_batches?.length > 0 && (
                          <div className="border rounded-lg p-4 mt-2">
                            <h4 className="font-medium text-sm mb-2">Next Batches Preview</h4>
                            <div className="space-y-2">
                              {queuePreview.next_batches.map((batch, idx) => (
                                <div key={idx} className="flex justify-between text-sm py-1 border-b last:border-0">
                                  <span className="text-gray-600">Batch {batch.batch_number}</span>
                                  <span>{batch.recipient_count} recipients</span>
                                  <span className="text-gray-500">
                                    {new Date(batch.scheduled_time).toLocaleTimeString()}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Warning */}
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-amber-800">
                            Sends are individualized; each recipient receives a personalized email one at a time. 
                            No group emailing or BCC is used.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Email Tracking Settings */}
                    <div className="pt-4 border-t mt-4">
                      <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                        <Eye className="w-4 h-4" />
                        Email Tracking
                      </h4>
                      <div className="space-y-3">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={deliverySettings.enable_open_tracking}
                            onChange={(e) => setDeliverySettings(prev => ({
                              ...prev,
                              enable_open_tracking: e.target.checked
                            }))}
                            className="rounded border-gray-300 text-[#00457f] focus:ring-[#00457f]"
                            data-testid="enable-open-tracking-checkbox"
                          />
                          <div>
                            <span className="text-sm font-medium text-gray-700">Track Opens</span>
                            <p className="text-xs text-gray-500">Invisible pixel to track when emails are opened</p>
                          </div>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={deliverySettings.enable_click_tracking}
                            onChange={(e) => setDeliverySettings(prev => ({
                              ...prev,
                              enable_click_tracking: e.target.checked
                            }))}
                            className="rounded border-gray-300 text-[#00457f] focus:ring-[#00457f]"
                            data-testid="enable-click-tracking-checkbox"
                          />
                          <div>
                            <span className="text-sm font-medium text-gray-700">Track Clicks</span>
                            <p className="text-xs text-gray-500">Track which links recipients click</p>
                          </div>
                        </label>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Test Email */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Send Test Email</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex gap-2">
                      <Input
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        placeholder="you@example.com, michelle@example.com, sara@example.com"
                        type="text"
                        data-testid="test-email-input"
                      />
                      <Button
                        onClick={sendTestEmail}
                        disabled={sendingTest || !testEmail}
                        data-testid="send-test-btn"
                      >
                        {sendingTest ? 'Sending...' : 'Send Test'}
                      </Button>
                    </div>
                    <p className="text-sm text-gray-500">
                      Send to multiple addresses by separating with commas. Test emails are prefixed with [TEST] in the subject line.
                    </p>
                    
                    {/* Test with Random Recipient */}
                    {recipients.length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-sm font-medium flex items-center gap-2">
                            <UserCheck className="w-4 h-4 text-amber-600" />
                            Test with Personalization
                          </Label>
                        </div>
                        <p className="text-xs text-gray-500 mb-3">
                          Send a test email using a random recipient's data to preview mail merge tokens
                        </p>
                        <Button
                          variant="outline"
                          onClick={sendTestWithRandomRecipient}
                          disabled={sendingTest || !testEmail}
                          className="w-full border-amber-200 text-amber-700 hover:bg-amber-50"
                          data-testid="test-random-recipient-btn"
                        >
                          {sendingTest ? (
                            <>
                              <div className="w-4 h-4 border-2 border-amber-400/30 border-t-amber-600 rounded-full animate-spin mr-2" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 mr-2" />
                              Test with Random Recipient Data
                            </>
                          )}
                        </Button>
                        {testRecipientUsed && (
                          <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Last test used: {testRecipientUsed.email} ({testRecipientUsed.first_name || 'No name'})
                          </p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Step Navigation - Final Step */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <Button variant="outline" onClick={handleBack}>
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Back to Recipients
                  </Button>
                  <div className="text-sm">
                    {!formData.from_email?.trim() ? (
                      <span className="text-amber-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        Select or enter a sender email
                      </span>
                    ) : (
                      <span className="text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        Ready to review & send
                      </span>
                    )}
                  </div>
                  <Button 
                    onClick={async () => {
                      if (!formData.from_email?.trim()) {
                        toast.error('Please select or enter a sender email');
                        return;
                      }
                      // Save campaign before navigating to review
                      try {
                        setSaving(true);
                        const response = await fetch(`${API_URL}/api/email/campaigns/${campaignId}`, {
                          method: 'PUT',
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({
                            ...formData,
                            email_styles: JSON.stringify(emailStyles),
                            delivery_mode: deliverySettings.delivery_mode,
                            batch_size: deliverySettings.batch_size,
                            interval_minutes: deliverySettings.interval_minutes,
                            start_at: deliverySettings.start_at,
                            sending_window_start: deliverySettings.sending_window_start || null,
                            sending_window_end: deliverySettings.sending_window_end || null,
                            max_per_day: deliverySettings.max_per_day || null,
                            timezone: deliverySettings.timezone,
                            warmup_preset: deliverySettings.warmup_preset,
                            enable_open_tracking: deliverySettings.enable_open_tracking,
                            enable_click_tracking: deliverySettings.enable_click_tracking
                          })
                        });
                        
                        if (!response.ok) {
                          throw new Error('Failed to save');
                        }
                        
                        setCompletedSteps(prev => ({ ...prev, settings: true }));
                        navigate(`/admin/email/review/${campaignId}`);
                      } catch (error) {
                        toast.error('Failed to save campaign. Please try again.');
                      } finally {
                        setSaving(false);
                      }
                    }}
                    disabled={saving}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {saving ? (
                      <>Saving...</>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-1" />
                        Review & Send
                      </>
                    )}
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Panel - Preview */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    Live Preview
                  </CardTitle>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant={previewDevice === 'desktop' ? 'default' : 'outline'}
                      onClick={() => setPreviewDevice('desktop')}
                    >
                      <Monitor className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant={previewDevice === 'mobile' ? 'default' : 'outline'}
                      onClick={() => setPreviewDevice('mobile')}
                    >
                      <Smartphone className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setFullscreenPreview(true)}
                      title="Full Screen Preview"
                    >
                      <Maximize2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {/* Recipient Preview Selector */}
                {recipients.length > 0 && (
                  <div className="flex items-center gap-2 mt-2">
                    <Label className="text-xs text-gray-500">Preview as:</Label>
                    <select
                      onChange={(e) => {
                        const recipientId = e.target.value;
                        if (recipientId) {
                          fetchMergePreview(recipientId);
                        } else {
                          setPreviewRecipient(null);
                          setMergedPreview(null);
                        }
                      }}
                      className="flex-1 text-xs px-2 py-1 border rounded"
                    >
                      <option value="">Select recipient...</option>
                      {recipients.slice(0, 50).map(r => (
                        <option key={r.id} value={r.id}>
                          {r.email} {r.first_name ? `(${r.first_name})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <div className="bg-gray-100 rounded-lg p-4 overflow-hidden">
                  {/* Recipient Preview Badge */}
                  {previewRecipient && mergedPreview && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mb-2 flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs">
                        <UserCheck className="w-4 h-4 text-amber-600" />
                        <span className="text-amber-700">
                          Previewing as: <strong>{previewRecipient.email}</strong>
                          {previewRecipient.first_name && ` (${previewRecipient.first_name})`}
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          setPreviewRecipient(null);
                          setMergedPreview(null);
                        }}
                        className="text-amber-600 hover:text-amber-800"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  
                  {/* Email Header Preview */}
                  <div className="bg-white rounded-t-lg p-3 border-b">
                    <p className="text-xs text-gray-500">From: {formData.from_name} &lt;{formData.from_email}&gt;</p>
                    <p className="font-semibold text-sm mt-1">
                      {mergedPreview ? mergedPreview.merged.subject : (formData.subject || 'No subject')}
                    </p>
                    {(mergedPreview?.merged.preheader || formData.preheader) && (
                      <p className="text-xs text-gray-400 truncate">
                        {mergedPreview ? mergedPreview.merged.preheader : formData.preheader}
                      </p>
                    )}
                  </div>
                  
                  {/* Email Body Preview */}
                  <div
                    className={`bg-white rounded-b-lg overflow-auto transition-all ${
                      previewDevice === 'mobile' ? 'max-w-[320px] mx-auto' : 'w-full'
                    }`}
                    style={{ maxHeight: '600px' }}
                  >
                    <div
                      dangerouslySetInnerHTML={{ __html: mergedPreview ? buildPreviewHtmlWithMerge(mergedPreview.merged.html_body) : buildPreviewHtml() }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Fullscreen Preview Modal */}
      {fullscreenPreview && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[95vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gray-50 rounded-t-lg">
              <div className="flex items-center gap-4">
                <h2 className="font-semibold text-lg">Email Preview</h2>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant={previewDevice === 'desktop' ? 'default' : 'outline'}
                    onClick={() => setPreviewDevice('desktop')}
                  >
                    <Monitor className="w-4 h-4 mr-1" />
                    Desktop
                  </Button>
                  <Button
                    size="sm"
                    variant={previewDevice === 'mobile' ? 'default' : 'outline'}
                    onClick={() => setPreviewDevice('mobile')}
                  >
                    <Smartphone className="w-4 h-4 mr-1" />
                    Mobile
                  </Button>
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setFullscreenPreview(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Modal Content */}
            <div className="flex-1 overflow-auto p-6 bg-gray-100">
              <div className={`mx-auto transition-all ${previewDevice === 'mobile' ? 'max-w-[375px]' : 'max-w-[700px]'}`}>
                {/* Email Envelope */}
                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                  {/* Email Header */}
                  <div className="p-4 border-b bg-gray-50">
                    <p className="text-sm text-gray-500">
                      <span className="font-medium">From:</span> {formData.from_name} &lt;{formData.from_email}&gt;
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      <span className="font-medium">Subject:</span> {formData.subject || '(No subject)'}
                    </p>
                    {formData.preheader && (
                      <p className="text-xs text-gray-400 mt-1 italic">{formData.preheader}</p>
                    )}
                  </div>
                  
                  {/* Email Body */}
                  <div
                    className="email-preview-content"
                    dangerouslySetInnerHTML={{ __html: buildPreviewHtml() }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSV Column Mapping Modal */}
      {showMappingModal && csvPreview && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[95vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b bg-[#00457f] text-white rounded-t-lg">
              <div className="flex items-center gap-3">
                <Table className="w-5 h-5" />
                <div>
                  <h2 className="font-semibold text-lg">Map CSV Columns</h2>
                  <p className="text-sm text-white/70">{csvPreview.total_rows} rows detected</p>
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setShowMappingModal(false);
                  setCsvPreview(null);
                  setCsvFile(null);
                }}
                className="text-white hover:bg-white/10"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Modal Body */}
            <div className="flex-1 overflow-auto p-6 space-y-6">
              {/* Column Mapping */}
              <div>
                <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                  <Settings2 className="w-4 h-4" />
                  Map Your Columns
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Select which CSV column corresponds to each field. Email is required.
                </p>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {[
                    { field: 'email', label: 'Email Address *', required: true },
                    { field: 'full_name', label: 'Full Name' },
                    { field: 'first_name', label: 'First Name' },
                    { field: 'last_name', label: 'Last Name' },
                    { field: 'title', label: 'Title/Role' },
                    { field: 'company', label: 'Company' },
                  ].map(({ field, label, required }) => (
                    <div key={field}>
                      <Label className={`text-sm ${required ? 'font-semibold' : ''}`}>
                        {label}
                      </Label>
                      <select
                        value={columnMapping[field] ?? ''}
                        onChange={(e) => {
                          const val = e.target.value === '' ? null : parseInt(e.target.value);
                          setColumnMapping(prev => ({ ...prev, [field]: val }));
                        }}
                        className={`w-full mt-1 px-3 py-2 border rounded-md text-sm ${
                          required && (columnMapping[field] === null || columnMapping[field] === undefined)
                            ? 'border-red-300 bg-red-50'
                            : 'border-gray-200'
                        }`}
                      >
                        <option value="">-- Not mapped --</option>
                        {csvPreview.headers.map((header, idx) => (
                          <option key={idx} value={idx}>
                            {header}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>

                {/* Auto-split name toggle */}
                <div className="flex items-center gap-3 mt-4 p-3 bg-gray-50 rounded-lg">
                  <Switch
                    checked={autoSplitName}
                    onCheckedChange={setAutoSplitName}
                  />
                  <div>
                    <Label className="text-sm font-medium">Auto-split Full Name</Label>
                    <p className="text-xs text-gray-500">
                      If first/last name aren't mapped, split "Full Name" into first and last
                    </p>
                  </div>
                </div>
              </div>

              {/* Preview Table */}
              <div>
                <h3 className="font-semibold text-sm mb-3">Preview (first {csvPreview.preview_rows.length} rows)</h3>
                <div className="border rounded-lg overflow-auto max-h-[250px]">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        {csvPreview.headers.map((header, idx) => (
                          <th key={idx} className="text-left p-2 border-b font-medium text-gray-600 whitespace-nowrap">
                            {header}
                            {columnMapping.email === idx && (
                              <span className="ml-1 text-xs bg-green-100 text-green-700 px-1 rounded">Email</span>
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {csvPreview.preview_rows.map((row, rowIdx) => (
                        <tr key={rowIdx} className="border-b hover:bg-gray-50">
                          {row.map((cell, cellIdx) => (
                            <td key={cellIdx} className="p-2 whitespace-nowrap">
                              {cell || <span className="text-gray-300">-</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Import Summary */}
              {importResult && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Import Complete
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Valid: {importResult.valid}
                    </div>
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-500" />
                      Invalid: {importResult.invalid}
                    </div>
                    <div className="text-gray-500">Duplicates: {importResult.duplicates}</div>
                    <div className="text-gray-500">Suppressed: {importResult.suppressed}</div>
                  </div>
                  {importResult.invalid_rows?.length > 0 && (
                    <div className="mt-2 text-sm text-red-600">
                      Invalid rows: {importResult.invalid_rows.slice(0, 3).map(r => `Row ${r.row}: ${r.error}`).join(', ')}
                      {importResult.invalid_rows.length > 3 && ` (+${importResult.invalid_rows.length - 3} more)`}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="flex items-center justify-between p-4 border-t bg-gray-50 rounded-b-lg">
              <Button
                variant="outline"
                onClick={() => {
                  setShowMappingModal(false);
                  setCsvPreview(null);
                  setCsvFile(null);
                  setImportResult(null);
                }}
              >
                Cancel
              </Button>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">
                  {csvPreview.total_rows} rows will be processed
                </span>
                <Button
                  onClick={importWithMapping}
                  disabled={importingMapped || columnMapping.email === null || columnMapping.email === undefined}
                  className="bg-[#00457f] hover:bg-[#003366]"
                >
                  {importingMapped ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Import Recipients
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailEditor;
